(() => {
  const root = document.documentElement;
  const body = document.body;
  const statusRegion = document.querySelector('[data-theme-status]');
  const desktopHoverMedia = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
  const desktopSmoothMedia = window.matchMedia('(min-width: 768px)');
  const mobileViewportMedia = window.matchMedia('(max-width: 767px)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sectionMarkupCache = new Map();
  const featureInitializers = new Map();
  const featureCleanups = new WeakMap();
  const conditionalFeatureLoads = new Map();
  const smoothScrollUpdateCallbacks = new Set();
  const detailsAnimations = new WeakMap();
  const conditionalFeatureDefinitions = [
    {
      name: 'product-features',
      selector: '[data-product-section-id], [data-product-recommendations-root], [data-recently-viewed-products]',
      source: () => body.dataset.themeProductFeaturesSrc,
    },
    {
      name: 'media-features',
      selector: '[data-media-surface], [data-slideshow], [data-basic-content-video], [data-video-section], [data-basic-content-fading-slideshow], [data-scrolling-promotion], [data-infinite-image-gallery], [data-horizontal-scroll-section], .theme-image-cards, [data-icon-boxes-scroll], [data-product-media-zoom]',
      source: () => body.dataset.themeMediaFeaturesSrc,
    },
  ];
  let animationObserver;
  let scrollSmoother;
  let smoothHeaderResizeObserver;
  let smoothHeaderLayoutHeight = null;
  let smoothScrollListenersBound = false;
  let smoothScrollAnchorHandlerBound = false;

  root.classList.add('theme-ready');

  if (window.Shopify?.designMode) {
    body.dataset.designMode = 'true';
  }

  function collect(scope, selector) {
    const nodes = [];

    if (scope instanceof Element && scope.matches(selector)) {
      nodes.push(scope);
    }

    if (scope.querySelectorAll) {
      nodes.push(...scope.querySelectorAll(selector));
    }

    return nodes;
  }

  function initRegisteredFeatures(scope = document) {
    featureInitializers.forEach((initializer) => {
      initializer(scope);
    });
  }

  function registerFeature(name, initializer) {
    const featureName = String(name || '').trim();

    if (!featureName || typeof initializer !== 'function' || featureInitializers.has(featureName)) {
      return;
    }

    featureInitializers.set(featureName, initializer);

    if (document.readyState !== 'loading') {
      initializer(document);
    }
  }

  function registerCleanup(element, callback) {
    if (!(element instanceof Element) || typeof callback !== 'function') return;

    const callbacks = featureCleanups.get(element) || [];
    callbacks.push(callback);
    featureCleanups.set(element, callbacks);
    element.dataset.themeCleanupBound = 'true';
  }

  function registerSmoothScrollUpdate(callback) {
    if (typeof callback !== 'function') return () => {};

    smoothScrollUpdateCallbacks.add(callback);
    return () => smoothScrollUpdateCallbacks.delete(callback);
  }

  function cleanupScope(scope) {
    collect(scope, '[data-theme-cleanup-bound]').forEach((element) => {
      const callbacks = featureCleanups.get(element) || [];

      callbacks.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.warn('Unable to clean up a theme feature.', error);
        }
      });

      featureCleanups.delete(element);
      delete element.dataset.themeCleanupBound;
    });

    collect(scope, '[data-animate]').forEach((element) => animationObserver?.unobserve(element));
    collect(scope, '[data-animate]').forEach((element) => {
      window.gsap?.killTweensOf(element);
      const wrapper = element.closest('.shopify-section');
      wrapper?.querySelectorAll(':scope > [data-animate-decoration]').forEach((decoration) => {
        window.gsap?.killTweensOf(decoration);
      });
    });
  }

  function loadConditionalFeature({ name, selector, source }, scope = document) {
    if (featureInitializers.has(name) || conditionalFeatureLoads.has(name)) return;
    if (collect(scope, selector).length === 0) return;

    const assetUrl = String(source?.() || '').trim();
    if (!assetUrl) return;

    const importUrl = assetUrl.startsWith('//') ? `https:${assetUrl}` : assetUrl;

    const loadPromise = import(importUrl).catch((error) => {
      conditionalFeatureLoads.delete(name);
      console.warn(`Unable to load ${name}.`, error);
    });

    conditionalFeatureLoads.set(name, loadPromise);
  }

  function loadConditionalFeatures(scope = document) {
    conditionalFeatureDefinitions.forEach((definition) => loadConditionalFeature(definition, scope));
  }

  function announce(message) {
    if (!statusRegion || !message) return;

    statusRegion.textContent = '';
    window.requestAnimationFrame(() => {
      statusRegion.textContent = message;
    });
  }

  function formatMoney(cents) {
    const locale = body.dataset.currencyLocale || 'en-US';
    const currencyCode = body.dataset.currencyCode || 'USD';
    const currencyDisplay = body.dataset.currencyDisplay || 'symbol';
    const value = Number(cents || 0) / 100;

    return new Intl.NumberFormat(locale, {
      currency: currencyCode,
      currencyDisplay,
      style: 'currency',
    }).format(value);
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };

      return entities[character] || character;
    });
  }

  function getLocaleRoot() {
    const localeRoot = String(window?.Shopify?.routes?.root || '/');

    return localeRoot.endsWith('/') ? localeRoot : `${localeRoot}/`;
  }

  async function parseJsonResponse(response, {
    invalidResponseMessage = 'Unable to complete this request right now.',
    passwordProtectedMessage = 'This feature is unavailable from password-protected previews.',
  } = {}) {
    const responseUrl = String(response?.url || '');
    const contentType = String(response?.headers?.get('content-type') || '').toLowerCase();
    const isJsonLikeResponse = (
      contentType.includes('json')
      || contentType.includes('javascript')
      || contentType.includes('ecmascript')
    );

    if (response.redirected && /\/password(?:[?#]|$)/.test(responseUrl)) {
      throw {
        code: 'password_protected_preview',
        error: passwordProtectedMessage,
      };
    }

    if (!isJsonLikeResponse) {
      let responseText = '';

      try {
        responseText = await response.text();
      } catch (error) {
        responseText = '';
      }

      if (
        /\/password(?:[?#]|$)/.test(responseUrl)
        || /<title>[^<]*password/i.test(responseText)
      ) {
        throw {
          code: 'password_protected_preview',
          error: passwordProtectedMessage,
        };
      }

      try {
        const data = JSON.parse(responseText);

        if (!response.ok) {
          throw data;
        }

        return data;
      } catch (error) {
        if (error && typeof error === 'object' && !Array.isArray(error) && ('error' in error || 'errors' in error || 'code' in error)) {
          throw error;
        }

        throw {
          code: 'invalid_response',
          error: invalidResponseMessage,
        };
      }
    }

    let data;

    try {
      data = await response.json();
    } catch (error) {
      throw {
        code: 'invalid_json',
        error: invalidResponseMessage,
      };
    }

    if (!response.ok) {
      throw data;
    }

    return data;
  }

  function buildStoreProductUrl(handle) {
    const normalizedHandle = String(handle || '').trim();

    if (!normalizedHandle) return '';

    return `${getLocaleRoot()}products/${encodeURIComponent(normalizedHandle)}`;
  }

  function extractStoreProductImage(product = {}) {
    const imageCandidates = [
      product?.featured_image,
      product?.featuredImage,
      product?.image,
      Array.isArray(product?.images) ? product.images[0] : '',
      product?.featured_media?.preview_image,
      product?.featuredMedia?.previewImage,
    ];

    for (const candidate of imageCandidates) {
      if (!candidate) continue;

      if (typeof candidate === 'string') {
        return {
          imageAlt: String(product?.title || '').trim(),
          imageUrl: candidate,
        };
      }

      const imageUrl = String(candidate?.url || candidate?.src || '').trim();

      if (!imageUrl) continue;

      return {
        imageAlt: String(candidate?.alt || product?.title || '').trim(),
        imageUrl,
      };
    }

    return {
      imageAlt: '',
      imageUrl: '',
    };
  }

  async function loadPredictiveSearchResults({ query, types, limit = 6, signal } = {}) {
    const requestUrl = new URL(`${getLocaleRoot()}search/suggest.json`, window.location.origin);
    const normalizedQuery = String(query || '').trim();
    const normalizedTypes = String(types || 'product,collection,page,article,query').trim();

    requestUrl.searchParams.set('q', normalizedQuery);
    requestUrl.searchParams.set('resources[type]', normalizedTypes);
    requestUrl.searchParams.set('resources[limit]', String(Math.min(10, Math.max(1, Number(limit) || 6))));
    requestUrl.searchParams.set('resources[limit_scope]', 'each');
    requestUrl.searchParams.set('resources[options][unavailable_products]', 'hide');
    requestUrl.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

    const response = await fetch(requestUrl.toString(), {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal,
    });

    return parseJsonResponse(response, {
      invalidResponseMessage: 'Unable to search right now.',
      passwordProtectedMessage: 'Search is unavailable in password-protected previews. Open the storefront after entering the password.',
    });
  }

  function renderPredictiveSearchProduct(product = {}) {
    const title = String(product?.title || '').trim();
    const url = String(product?.url || buildStoreProductUrl(product?.handle)).trim();

    if (!title || !url) return '';

    const { imageAlt, imageUrl } = extractStoreProductImage(product);
    const productPrice = Number.parseFloat(product?.price);
    const price = Number.isFinite(productPrice)
      ? formatMoney(Math.round(productPrice * 100))
      : '';
    const mediaHtml = imageUrl
      ? `
        <span class="theme-header__predictive-product-media">
          <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt || title)}" loading="lazy">
        </span>
      `
      : '<span class="theme-header__predictive-product-media"></span>';

    return `
      <a class="theme-header__predictive-product" href="${escapeHtml(url)}">
        ${mediaHtml}
        <span class="theme-header__predictive-meta">
          <span class="theme-header__predictive-item-title">${escapeHtml(title)}</span>
          ${price ? `<span class="theme-header__predictive-item-copy">${escapeHtml(price)}</span>` : ''}
        </span>
      </a>
    `;
  }

  function renderPredictiveSearchLink(item = {}, typeLabel = '') {
    const title = String(item?.title || '').trim();
    const url = String(item?.url || '').trim();

    if (!title || !url) return '';

    return `
      <a class="theme-header__predictive-link" href="${escapeHtml(url)}">
        <span class="theme-header__predictive-item-title">${escapeHtml(title)}</span>
        ${typeLabel ? `<span class="theme-header__predictive-link-type">${escapeHtml(typeLabel)}</span>` : ''}
      </a>
    `;
  }

  function renderPredictiveSearchResults(data, { query, searchUrl, searchFormTypes } = {}) {
    const resultGroups = data?.resources?.results || {};
    const products = Array.isArray(resultGroups?.products) ? resultGroups.products : [];
    const collections = Array.isArray(resultGroups?.collections) ? resultGroups.collections : [];
    const pages = Array.isArray(resultGroups?.pages) ? resultGroups.pages : [];
    const articles = Array.isArray(resultGroups?.articles) ? resultGroups.articles : [];
    const productMarkup = products.map((product) => renderPredictiveSearchProduct(product)).filter(Boolean).join('');
    const linkMarkup = [
      ...collections.map((collection) => renderPredictiveSearchLink(collection, 'Collection')),
      ...pages.map((page) => renderPredictiveSearchLink(page, 'Page')),
      ...articles.map((article) => renderPredictiveSearchLink(article, 'Article')),
    ].filter(Boolean).join('');
    let viewAllUrl = '';

    try {
      const url = new URL(searchUrl || `${getLocaleRoot()}search`, window.location.origin);
      url.searchParams.set('q', String(query || '').trim());
      url.searchParams.set('type', String(searchFormTypes || 'product,page').trim());
      viewAllUrl = url.toString();
    } catch (error) {
      viewAllUrl = `${searchUrl || `${getLocaleRoot()}search`}?q=${encodeURIComponent(String(query || '').trim())}`;
    }

    if (!productMarkup && !linkMarkup) {
      return `
        <div class="theme-header__predictive-empty">
          <p class="theme-header__predictive-copy">No results found for "${escapeHtml(query)}".</p>
          <a class="button button--outline button--small" href="${escapeHtml(viewAllUrl)}">View full search</a>
        </div>
      `;
    }

    return `
      ${productMarkup ? `
        <div class="theme-header__predictive-group theme-header__predictive-group--products">
          <p class="theme-header__predictive-title">Products</p>
          <div class="theme-header__predictive-products">${productMarkup}</div>
        </div>
      ` : ''}
      ${linkMarkup ? `
        <div class="theme-header__predictive-group theme-header__predictive-group--links">
          <p class="theme-header__predictive-title">More results</p>
          <div class="theme-header__predictive-links">${linkMarkup}</div>
        </div>
      ` : ''}
      <a class="button button--primary button--small theme-header__predictive-view-all" href="${escapeHtml(viewAllUrl)}">View all results</a>
    `;
  }

  async function fetchSectionMarkup(url, sectionId, { force = false } = {}) {
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);
    const cacheKey = requestUrl.toString();

    if (!force && sectionMarkupCache.has(cacheKey)) {
      return sectionMarkupCache.get(cacheKey);
    }

    const requestPromise = (async () => {
      const response = await fetch(requestUrl.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load product details.');
      }

      return response.text();
    })();

    sectionMarkupCache.set(cacheKey, requestPromise);

    try {
      return await requestPromise;
    } catch (error) {
      sectionMarkupCache.delete(cacheKey);
      throw error;
    }
  }

  function getDialogAnimationDuration(dialog) {
    if (!dialog || reducedMotionMedia.matches) return 0;

    return Math.max(0, Number(dialog.dataset.dialogDuration || 220));
  }

  function isMobileHeaderDrawer(dialog) {
    return Boolean(
      dialog?.matches?.('[data-header-drawer]')
      && window.matchMedia('(max-width: 989px)').matches
    );
  }

  function setMobileHeaderDrawerState(dialog, isOpen) {
    if (!dialog?.matches?.('[data-header-drawer]')) return;

    const sectionId = String(dialog.dataset.headerDrawerFor || '').trim();
    const header = sectionId ? document.getElementById(`KSHeader-${sectionId}`) : null;

    if (!header) return;

    if (isOpen) {
      const drawerTop = Math.max(0, Math.round(header.getBoundingClientRect().bottom));

      dialog.style.setProperty('--theme-mobile-drawer-top', `${drawerTop}px`);
      header.dataset.mobileDrawerOpen = 'true';
      header.dataset.menuOpen = 'true';
      header.classList.remove('is-scrolled-down');
    } else {
      delete header.dataset.mobileDrawerOpen;
      dialog.style.removeProperty('--theme-mobile-drawer-top');

      if (!header.querySelector('[data-header-nav-item][data-nav-open="true"]')) {
        delete header.dataset.menuOpen;
      }
    }

    collect(header, `[aria-controls="${dialog.id}"][data-menu-open]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      trigger.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    });
  }

  function openDialog(dialog, { trigger = null } = {}) {
    if (!dialog || typeof dialog.showModal !== 'function') return;

    window.clearTimeout(dialog.__closeTimer);
    delete dialog.dataset.dialogClosing;

    if (trigger) {
      dialog.__returnFocusEl = trigger;

      if (trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    if (!dialog.open) {
      if (isMobileHeaderDrawer(dialog) && typeof dialog.show === 'function') {
        dialog.show();
      } else {
        dialog.showModal();
      }
    }

    setMobileHeaderDrawerState(dialog, true);
    dialog.dataset.dialogState = 'opening';

    window.requestAnimationFrame(() => {
      dialog.dataset.dialogState = 'open';
      syncDrawerStacks(dialog);
      collect(dialog, '[data-cart-drawer-scroll]').forEach((container) => {
        container.__updateCartDrawerScrollbar?.();
      });
    });
  }

  function closeDialog(dialog) {
    if (!dialog || typeof dialog.close !== 'function' || !dialog.open) return;
    if (dialog.dataset.dialogClosing === 'true') return;

    const duration = getDialogAnimationDuration(dialog);

    dialog.dataset.dialogClosing = 'true';
    dialog.dataset.dialogState = 'closing';
    setMobileHeaderDrawerState(dialog, false);

    const finalize = () => {
      if (dialog.open) {
        dialog.close();
      }
    };

    if (duration === 0) {
      finalize();
      return;
    }

    dialog.__closeTimer = window.setTimeout(finalize, duration);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load cart.');
    }

    return response.json();
  }

  function updateCartCount(count) {
    collect(document, '[data-cart-count]').forEach((node) => {
      const normalizedCount = Number(count || 0);

      node.textContent = normalizedCount > 99 ? '99+' : normalizedCount;
      node.hidden = normalizedCount === 0;
    });
  }

  function isUpcartAvailable() {
    return typeof window.upcartOpenCart === 'function';
  }

  function openUpcartCart() {
    if (!isUpcartAvailable()) return false;

    const nativeDrawer = document.querySelector('[data-cart-drawer-root][open]');
    if (nativeDrawer) closeDialog(nativeDrawer);

    window.upcartOpenCart();
    return true;
  }

  function subscribeToUpcartCart() {
    if (document.documentElement.dataset.upcartCartSubscribed === 'true') return true;
    if (typeof window.upcartSubscribeCartLoaded !== 'function') return false;

    window.upcartSubscribeCartLoaded((event) => {
      const items = event?.cart?.items;
      if (!Array.isArray(items)) return;

      const itemCount = items.reduce((total, item) => (
        total + Number(item?.quantity || 0)
      ), 0);

      updateCartCount(itemCount);
    });

    document.documentElement.dataset.upcartCartSubscribed = 'true';
    fetchCart().then((cart) => updateCartCount(cart.item_count)).catch(() => {});
    return true;
  }

  function initUpcartBridge(scope = document) {
    collect(scope, '[data-upcart-trigger]').forEach((trigger) => {
      if (trigger.dataset.upcartBridgeInitialized === 'true') return;
      trigger.dataset.upcartBridgeInitialized = 'true';

      trigger.addEventListener('click', (event) => {
        if (!isUpcartAvailable()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        openUpcartCart();
      }, true);
    });

    if (subscribeToUpcartCart()) return;
    if (document.documentElement.dataset.upcartBridgePolling === 'true') return;

    document.documentElement.dataset.upcartBridgePolling = 'true';
    let attempts = 0;

    const retrySubscription = () => {
      attempts += 1;

      if (subscribeToUpcartCart() || attempts >= 100) {
        delete document.documentElement.dataset.upcartBridgePolling;
        return;
      }

      window.setTimeout(retrySubscription, 200);
    };

    window.setTimeout(retrySubscription, 0);
  }

  async function refreshHeaderCartState({ openDrawer = false } = {}) {
    const header = document.querySelector('[data-header-section-id]');
    const currentDrawer = document.querySelector('[data-cart-drawer-root]');

    if (!header) return;

    const markup = await fetchSectionMarkup(
      window.location.href,
      header.dataset.headerSectionId,
      { force: true }
    );
    const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
    const nextDrawer = parsedDocument.querySelector('[data-cart-drawer-root]');

    if (currentDrawer && nextDrawer) {
      currentDrawer.innerHTML = nextDrawer.innerHTML;
      delete currentDrawer.dataset.cartLoading;
      initScope(currentDrawer);

    }

    if (openDrawer && !openUpcartCart() && currentDrawer && !currentDrawer.open) {
      openDialog(currentDrawer);
    }
  }

  function openCartDrawerLoading(sourceForm = null) {
    if (isUpcartAvailable()) return null;

    const drawer = document.querySelector('[data-cart-drawer-root]');

    if (!drawer) return null;

    const productRoot = sourceForm?.closest('[data-product-section-id], [data-product-card]');
    const productTitle = productRoot?.querySelector(
      '[data-product-page-title], .product-card__title'
    )?.textContent?.trim();
    const productImage = productRoot?.querySelector(
      '.theme-product-media__item.is-active img, .product-card__image'
    );
    const loadingTitle = drawer.querySelector('[data-cart-loading-title]');
    const loadingMedia = drawer.querySelector('[data-cart-loading-media]');

    if (loadingTitle) {
      loadingTitle.textContent = productTitle || '';
    }

    if (loadingMedia) {
      const imageUrl = productImage?.currentSrc || productImage?.src || '';
      loadingMedia.style.backgroundImage = imageUrl ? `url("${imageUrl}")` : '';
    }

    drawer.dataset.cartLoading = 'true';
    openDialog(drawer);
    return drawer;
  }

  function initDialogs(scope = document) {
    collect(scope, '[data-dialog-trigger], [data-menu-open], [data-filter-open]').forEach((trigger) => {
      if (trigger.dataset.dialogInitialized === 'true') return;
      trigger.dataset.dialogInitialized = 'true';

      trigger.addEventListener('click', (event) => {
        const dialogId = trigger.getAttribute('data-dialog-trigger') || trigger.getAttribute('aria-controls');
        const dialog = dialogId ? document.getElementById(dialogId) : null;

        if (!dialog || typeof dialog.showModal !== 'function') return;

        event.preventDefault();

        if (dialog.open && isMobileHeaderDrawer(dialog)) {
          closeDialog(dialog);
          return;
        }

        openDialog(dialog, { trigger });
      });
    });

    collect(scope, '[data-dialog-close], [data-menu-close], [data-filter-close]').forEach((button) => {
      if (button.dataset.dialogInitialized === 'true') return;
      button.dataset.dialogInitialized = 'true';

      button.addEventListener('click', () => {
        const dialog = button.closest('dialog');
        if (dialog) closeDialog(dialog);
      });
    });

    collect(scope, 'dialog').forEach((dialog) => {
      if (dialog.dataset.dialogInitialized === 'true') return;
      dialog.dataset.dialogInitialized = 'true';

      dialog.addEventListener('click', (event) => {
        const panel = dialog.querySelector(
          '[data-dialog-panel], .theme-header__search-panel, .theme-header__drawer-panel, .theme-collection__drawer-panel, .theme-cart-drawer__panel'
        );
        if (panel && !panel.contains(event.target)) {
          closeDialog(dialog);
        }
      });

      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });

      dialog.addEventListener('close', () => {
        window.clearTimeout(dialog.__closeTimer);
        delete dialog.dataset.dialogClosing;
        delete dialog.dataset.dialogState;
        setMobileHeaderDrawerState(dialog, false);

        if (dialog.id) {
          collect(document, `[aria-controls="${dialog.id}"]`).forEach((trigger) => {
            if (trigger.hasAttribute('aria-expanded')) {
              trigger.setAttribute('aria-expanded', 'false');
            }
          });
        }

        if (dialog.__returnFocusEl && dialog.__returnFocusEl.isConnected) {
          dialog.__returnFocusEl.focus({ preventScroll: true });
        }

        collect(dialog, '[data-drawer-stack]').forEach((stack) => {
          resetDrawerStack(stack);
        });

        dialog.__returnFocusEl = null;
      });
    });
  }

  function initButtonVideoModals(scope = document) {
    collect(scope, '[data-video-modal]').forEach((dialog) => {
      if (dialog.dataset.videoModalInitialized === 'true') return;
      dialog.dataset.videoModalInitialized = 'true';

      const hostedVideo = dialog.querySelector('[data-modal-video]');
      const embed = dialog.querySelector('[data-video-embed]');
      const triggers = dialog.id
        ? collect(document, `[data-dialog-trigger="${dialog.id}"]`)
        : [];

      const startVideo = () => {
        if (embed && !embed.getAttribute('src')) {
          embed.setAttribute('src', embed.dataset.videoSrc || '');
        }

        hostedVideo?.play?.().catch(() => {});
      };

      const stopVideo = () => {
        if (hostedVideo) {
          hostedVideo.pause();
          hostedVideo.currentTime = 0;
        }

        embed?.removeAttribute('src');
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', startVideo);
      });

      dialog.addEventListener('close', stopVideo);
      registerCleanup(dialog, stopVideo);
    });
  }

  function normalizeHeaderMenuHandle(value = '') {
    return value
      .trim()
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function initHeaderMenuButtons(scope = document) {
    const header = scope.matches?.('[data-header-section-id]')
      ? scope
      : scope.querySelector?.('[data-header-section-id]');
    const rootScope = header || document;

    collect(rootScope, '[data-header-menu-button-source]').forEach((source) => {
      const blockId = source.dataset.headerMenuButtonBlockId || '';
      const targetHandle = source.dataset.headerMenuButtonTarget || '';
      const buttonStyle = source.dataset.headerMenuButtonStyle || 'primary';
      const buttonSize = source.dataset.headerMenuButtonSize || 'small';

      if (!blockId || !targetHandle) return;

      const mountedButtons = collect(rootScope, '[data-header-menu-button-mounted]').filter(
        (mountedButton) => mountedButton.dataset.headerMenuButtonMounted === blockId
      );
      let actionButton = mountedButtons.shift() || null;
      mountedButtons.forEach((mountedButton) => mountedButton.remove());

      collect(rootScope, '.theme-header__nav-item--action-source').forEach((navItem) => {
        if (navItem.dataset.headerMenuButtonBlockId === blockId) {
          navItem.classList.remove('theme-header__nav-item--action-source');
          navItem.hidden = false;
          delete navItem.dataset.headerMenuButtonBlockId;
        }
      });

      collect(rootScope, '.theme-header__menu-link--action-source').forEach((link) => {
        if (link.dataset.headerMenuButtonBlockId === blockId) {
          link.classList.remove('theme-header__menu-link--action-source');
          link.hidden = false;
          delete link.dataset.headerMenuButtonBlockId;
        }
      });

      const matchingLinks = [];

      collect(rootScope, '[data-header-nav-handle]').forEach((navItem) => {
        if (navItem.dataset.headerNavHandle !== targetHandle) return;

        const link = navItem.querySelector(':scope > .theme-header__nav-link');
        if (!link) return;

        matchingLinks.push(link);
        navItem.classList.add('theme-header__nav-item--action-source');
        navItem.hidden = true;
        navItem.dataset.headerMenuButtonBlockId = blockId;
      });

      collect(rootScope, '.theme-header__nav--secondary a, .theme-header__drawer-link').forEach((link) => {
        if (normalizeHeaderMenuHandle(link.textContent) !== targetHandle) return;

        matchingLinks.push(link);
        link.classList.add('theme-header__menu-link--action-source');
        link.dataset.headerMenuButtonBlockId = blockId;

        if (link.closest('.theme-header__nav--secondary')) link.hidden = true;
      });

      const actions = rootScope.querySelector('.theme-header__actions');
      const linkedMenuItem = matchingLinks.find((link) => link.getAttribute('href'));
      if (!actions || !linkedMenuItem) {
        actionButton?.remove();
        return;
      }

      if (!actionButton) actionButton = document.createElement('a');
      actionButton.href = linkedMenuItem.getAttribute('href');
      actionButton.textContent = source.textContent.trim() || linkedMenuItem.textContent.trim();
      actionButton.dataset.headerMenuButtonMounted = blockId;
      actionButton.dataset.headerActionTarget = targetHandle;
      actionButton.className = `button button--${buttonStyle} button--${buttonSize} theme-header__action-button`;
      if (!actions.contains(actionButton)) actions.prepend(actionButton);
    });
  }

  function initHeaderMenus(scope = document) {
    collect(scope, '[data-header-nav-item]').forEach((item) => {
      if (item.dataset.headerNavInitialized === 'true') return;
      item.dataset.headerNavInitialized = 'true';

      const trigger = item.querySelector('.theme-header__nav-link');
      const submenu = item.querySelector('.theme-header__submenu');
      let closeTimer = 0;
      let clearActiveNestedPanel = () => {};

      if (!trigger) return;

      const clearCloseTimer = () => {
        if (!closeTimer) return;
        window.clearTimeout(closeTimer);
        closeTimer = 0;
      };

      const setOpenState = (isOpen) => {
        const isMegaMenu = item.classList.contains('theme-header__nav-item--mega');

        if (isOpen && submenu?.classList.contains('theme-header__submenu--standard-mega')) {
          alignStandardMegaSubmenu(item, submenu);
        }

        item.dataset.navOpen = isOpen ? 'true' : 'false';
        setHeaderMenuOpenState(item, isOpen);
        setMegaMenuOverlayState(item, isOpen && isMegaMenu);

        if (trigger.hasAttribute('aria-expanded')) {
          trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }

        if (!isOpen) {
          clearActiveNestedPanel();
        }
      };

      const scheduleClose = () => {
        clearCloseTimer();
        closeTimer = window.setTimeout(() => {
          setOpenState(false);
        }, 140);
      };

      const closeSiblingMenus = () => {
        const header = item.closest('[data-header-section-id]');
        if (!header) return;

        collect(header, '[data-header-nav-item][data-nav-open="true"]').forEach((sibling) => {
          if (sibling === item) return;
          sibling.dispatchEvent(new CustomEvent('pegasus:header-nav-close'));
        });
      };

      item.addEventListener('pegasus:header-nav-close', () => {
        clearCloseTimer();
        setOpenState(false);
      });

      item.addEventListener('mouseenter', () => {
        closeSiblingMenus();
        clearCloseTimer();
        setOpenState(true);
      });

      item.addEventListener('mouseleave', () => {
        scheduleClose();
      });

      item.addEventListener('focusin', () => {
        closeSiblingMenus();
        clearCloseTimer();
        setOpenState(true);
      });

      item.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
          if (!item.contains(document.activeElement)) {
            clearCloseTimer();
            setOpenState(false);
          }
        });
      });

      if (submenu) {
        const nestedTriggers = Array.from(submenu.querySelectorAll('[data-header-submenu-trigger]'));
        const nestedPanels = Array.from(submenu.querySelectorAll('[data-header-submenu-panel]'));
        const nestedPanelContainer = submenu.querySelector('.theme-header__submenu-panels');
        const nestedPlainLinks = Array.from(submenu.querySelectorAll('.theme-header__submenu-list > .theme-header__submenu-item > .theme-header__submenu-link:not([data-header-submenu-trigger])'));

        clearActiveNestedPanel = () => {
          nestedTriggers.forEach((nestedTrigger) => {
            nestedTrigger.setAttribute('aria-expanded', 'false');
            nestedTrigger.closest('.theme-header__submenu-item')?.classList.remove('is-active');
          });

          nestedPanels.forEach((panel) => {
            panel.classList.remove('is-active');
          });

          nestedPanelContainer?.classList.remove('is-active');
        };

        const setActiveNestedPanel = (panelId) => {
          if (!panelId) return;

          nestedTriggers.forEach((nestedTrigger) => {
            const isActive = nestedTrigger.dataset.headerSubmenuTrigger === panelId;
            nestedTrigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
            nestedTrigger.closest('.theme-header__submenu-item')?.classList.toggle('is-active', isActive);
          });

          nestedPanels.forEach((panel) => {
            panel.classList.toggle('is-active', panel.dataset.headerSubmenuPanel === panelId);
          });

          nestedPanelContainer?.classList.add('is-active');
        };

        nestedTriggers.forEach((nestedTrigger) => {
          const panelId = nestedTrigger.dataset.headerSubmenuTrigger;

          nestedTrigger.addEventListener('mouseenter', () => {
            setActiveNestedPanel(panelId);
          });

          nestedTrigger.addEventListener('focus', () => {
            setActiveNestedPanel(panelId);
          });
        });

        nestedPlainLinks.forEach((nestedPlainLink) => {
          nestedPlainLink.addEventListener('mouseenter', clearActiveNestedPanel);
          nestedPlainLink.addEventListener('focus', clearActiveNestedPanel);
        });

        submenu.addEventListener('mouseenter', () => {
          clearCloseTimer();
          setOpenState(true);
        });

        submenu.addEventListener('mouseleave', () => {
          clearActiveNestedPanel();
          scheduleClose();
        });
      }

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        clearCloseTimer();
        setOpenState(false);
        trigger.focus({ preventScroll: true });
      });
    });
  }

  function cloneStandardMegaMenuSource(source) {
    const clone = source.cloneNode(true);

    clone.removeAttribute('data-standard-mega-source');
    delete clone.dataset.standardMegaSource;
    delete clone.dataset.standardMegaInitialized;

    return clone;
  }

  function initMobileStandardMegaMenu(menu) {
    if (!menu || menu.dataset.mobileStandardMegaInitialized === 'true') return;

    const screen = menu.closest('.theme-header__drawer-screen--standard-mega');
    const layout = menu.querySelector(':scope > .theme-header__standard-mega');

    if (!screen || !layout) return;

    menu.dataset.mobileStandardMegaInitialized = 'true';

    if (layout.classList.contains('theme-header__standard-mega--linked-image-cards')) {
      layout.classList.add('theme-header__standard-mega--mobile-linked-cards');
      return;
    }

    const columns = Array.from(layout.querySelectorAll(':scope > [data-standard-mega-column]'));
    const imageColumns = Array.from(layout.querySelectorAll(
      ':scope > [data-standard-mega-image-columns] > .theme-header__standard-mega-image-column'
    ));

    if (columns.length === 0) return;

    const heading = menu.dataset.standardMegaHeading || 'menu';
    const tabs = document.createElement('div');
    const sharedImages = document.createElement('div');
    const panelsShell = document.createElement('div');
    const panels = document.createElement('div');
    const panelsScrollbar = document.createElement('span');
    const panelsScrollbarIndicator = document.createElement('span');

    tabs.className = 'theme-header__standard-mega-mobile-tabs';
    tabs.setAttribute('role', 'tablist');
    tabs.setAttribute('aria-label', 'Menu categories');
    sharedImages.className = 'theme-header__standard-mega-mobile-images';
    panelsShell.className = 'theme-header__standard-mega-mobile-panels-shell';
    panels.className = 'theme-header__standard-mega-mobile-panels';
    panelsScrollbar.className = 'theme-header__standard-mega-mobile-panels-scrollbar';
    panelsScrollbar.setAttribute('aria-hidden', 'true');
    panelsScrollbarIndicator.className = 'theme-header__standard-mega-mobile-panels-scrollbar-indicator';
    panelsScrollbar.append(panelsScrollbarIndicator);
    panelsShell.append(panels, panelsScrollbar);

    let panelsScrollbarFrame = 0;
    const updatePanelsScrollbar = () => {
      window.cancelAnimationFrame(panelsScrollbarFrame);
      panelsScrollbarFrame = window.requestAnimationFrame(() => {
        const viewportHeight = panels.clientHeight;
        const contentHeight = panels.scrollHeight;
        const maxScroll = Math.max(0, contentHeight - viewportHeight);
        const isScrollable = viewportHeight > 0 && maxScroll > 1;

        panelsShell.classList.toggle('is-scrollable', isScrollable);

        if (!isScrollable) {
          panelsScrollbarIndicator.style.height = '';
          panelsScrollbarIndicator.style.transform = '';
          return;
        }

        const indicatorHeight = Math.max(36, viewportHeight * (viewportHeight / contentHeight));
        const availableTrack = Math.max(0, viewportHeight - indicatorHeight);
        const indicatorOffset = availableTrack * (panels.scrollTop / maxScroll);

        panelsScrollbarIndicator.style.height = `${indicatorHeight}px`;
        panelsScrollbarIndicator.style.transform = `translate3d(0, ${indicatorOffset}px, 0)`;
      });
    };

    panels.addEventListener('scroll', updatePanelsScrollbar, { passive: true });

    if (typeof ResizeObserver === 'function') {
      const panelsResizeObserver = new ResizeObserver(updatePanelsScrollbar);
      panelsResizeObserver.observe(panels);
      panelsShell.__pegasusStandardMegaPanelsResizeObserver = panelsResizeObserver;
    }

    if (typeof MutationObserver === 'function') {
      const panelsMutationObserver = new MutationObserver(updatePanelsScrollbar);
      panelsMutationObserver.observe(panels, { childList: true, subtree: true });
      panelsShell.__pegasusStandardMegaPanelsMutationObserver = panelsMutationObserver;
    }

    const activateTab = (activeIndex, focusTab = false) => {
      const tabButtons = Array.from(tabs.querySelectorAll('[role="tab"]'));
      const tabPanels = Array.from(panels.querySelectorAll('[role="tabpanel"]'));

      tabButtons.forEach((button, index) => {
        const isActive = index === activeIndex;

        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
        button.classList.toggle('is-active', isActive);

        if (isActive && focusTab) {
          button.focus({ preventScroll: true });
          button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });

      tabPanels.forEach((panel, index) => {
        panel.hidden = index !== activeIndex;
      });

      if (
        sharedImages.childElementCount > 0
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        sharedImages.classList.remove('is-switching');
        void sharedImages.offsetWidth;
        sharedImages.classList.add('is-switching');
      }

      window.requestAnimationFrame(() => {
        panels.scrollTop = 0;
        updatePanelsScrollbar();
        updateDrawerStackHeight(screen.closest('[data-drawer-stack]'));
      });
    };

    columns.forEach((column, index) => {
      const columnHandle = column.dataset.columnHandle || `category-${index + 1}`;
      const columnTitle = column.dataset.columnTitle || columnHandle;
      const tabId = `KSMobileMegaTab-${heading}-${index + 1}`;
      const panelId = `KSMobileMegaPanel-${heading}-${index + 1}`;
      const tab = document.createElement('button');
      const panel = document.createElement('section');

      tab.type = 'button';
      tab.id = tabId;
      tab.className = 'theme-header__standard-mega-mobile-tab';
      tab.textContent = columnTitle;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.addEventListener('click', () => activateTab(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + columns.length) % columns.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % columns.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = columns.length - 1;

        activateTab(nextIndex, true);
      });

      panel.id = panelId;
      panel.className = 'theme-header__standard-mega-mobile-panel';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.append(column);

      tabs.append(tab);
      panels.append(panel);
    });

    imageColumns.forEach((imageColumn) => sharedImages.append(imageColumn));

    const leadMobileImage = sharedImages.querySelector('img[data-deferred-image]');
    if (leadMobileImage) {
      leadMobileImage.loading = 'eager';
      hydrateDeferredImage(leadMobileImage);
    }

    sharedImages.addEventListener('animationend', (event) => {
      if (event.animationName === 'theme-standard-mega-mobile-images-in') {
        sharedImages.classList.remove('is-switching');
      }
    });

    if (sharedImages.childElementCount > 0) {
      layout.replaceChildren(tabs, panelsShell, sharedImages);
    } else {
      layout.replaceChildren(tabs, panelsShell);
    }

    activateTab(0);
    updatePanelsScrollbar();
  }

  function setMegaMenuOverlayState(navItem, isOpen) {
    const header = navItem?.closest?.('[data-header-section-id]');

    if (!header || header.dataset.megaMenuOverlay !== 'true') return;

    if (isOpen) {
      header.dataset.megaOverlayOpen = 'true';
      return;
    }

    const hasOpenMegaMenu = collect(header, '.theme-header__nav-item--mega[data-nav-open="true"]').length > 0;

    if (hasOpenMegaMenu) return;

    delete header.dataset.megaOverlayOpen;
  }

  function setHeaderMenuOpenState(navItem, isOpen) {
    const header = navItem?.closest?.('[data-header-section-id]');

    if (!header) return;

    if (isOpen) {
      header.dataset.menuOpen = 'true';
      return;
    }

    const hasOpenMenu = collect(header, '[data-header-nav-item][data-nav-open="true"]').length > 0;

    if (hasOpenMenu) return;

    delete header.dataset.menuOpen;
  }

  function alignStandardMegaSubmenu(navItem, submenu = navItem?.querySelector?.(':scope > .theme-header__submenu--standard-mega')) {
    if (!navItem || !submenu || !submenu.classList.contains('theme-header__submenu--standard-mega')) return;

    const navItemRect = navItem.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;

    submenu.style.setProperty('--theme-standard-mega-panel-left', `${navItemRect.left * -1}px`);
    submenu.style.setProperty('--theme-standard-mega-panel-width', `${Math.round(viewportWidth)}px`);
  }

  function alignStandardMegaSubmenus(scope = document) {
    collect(scope, '.theme-header__nav-item--mega').forEach((navItem) => {
      alignStandardMegaSubmenu(navItem);
    });
  }

  function upgradeStandardMegaNavItems(scope = document) {
    const header = scope.matches?.('[data-header-section-id]')
      ? scope
      : scope.querySelector?.('[data-header-section-id]');
    const rootScope = header || document;

    collect(rootScope, '[data-standard-mega-source]').forEach((source) => {
      const heading = source.dataset.standardMegaHeading;
      const sourceColorScheme = source.dataset.standardMegaColorScheme || '';
      if (!heading) return;

      collect(rootScope, '[data-header-nav-handle]').forEach((navItem) => {
        if (navItem.dataset.headerNavHandle !== heading) return;
        if (navItem.dataset.standardMegaNavUpgraded === 'true') return;

        let submenu = navItem.querySelector(':scope > .theme-header__submenu');
        const trigger = navItem.querySelector(':scope > .theme-header__nav-link');

        if (!submenu) {
          submenu = document.createElement('div');
          submenu.className = 'theme-header__submenu color-scheme-default';
          navItem.append(submenu);
        }

        navItem.classList.add('theme-header__nav-item--has-children', 'theme-header__nav-item--mega');
        navItem.dataset.headerNavItem = '';
        navItem.dataset.standardMegaNavUpgraded = 'true';
        delete navItem.dataset.headerNavInitialized;

        if (trigger) {
          trigger.setAttribute('aria-haspopup', 'true');
          trigger.setAttribute('aria-expanded', 'false');
        }

        submenu.classList.remove('theme-header__submenu--with-content', 'theme-header__submenu--nested');
        submenu.classList.add('theme-header__submenu--content-only', 'theme-header__submenu--standard-mega');
        if (sourceColorScheme) {
          Array.from(submenu.classList)
            .filter((className) => className.startsWith('color-'))
            .forEach((className) => submenu.classList.remove(className));
          submenu.classList.add(`color-${sourceColorScheme}`);
        }
        submenu.replaceChildren();

        const headerContainer = navItem.closest('[data-header-section-id]')?.querySelector('.theme-header__main > .theme-container');
        const wrapper = document.createElement('div');
        wrapper.className = headerContainer
          ? `theme-header__submenu-content ${headerContainer.className}`
          : 'theme-header__submenu-content theme-container theme-container--default';
        wrapper.append(cloneStandardMegaMenuSource(source));
        submenu.append(wrapper);

        alignStandardMegaSubmenu(navItem, submenu);
      });
    });
  }

  function initStandardMegaMenus(scope = document) {
    upgradeStandardMegaNavItems(scope);

    collect(scope, '[data-standard-mega-mount]').forEach((mount) => {
      if (mount.dataset.standardMegaMounted === 'true') return;

      const heading = mount.dataset.standardMegaHeading;
      const header = mount.closest('[data-header-section-id]') || document;
      const source = collect(header, '[data-standard-mega-source]').find((candidate) => (
        candidate.dataset.standardMegaHeading === heading
      ));

      if (!heading || !source) return;

      mount.replaceChildren(cloneStandardMegaMenuSource(source));
      mount.dataset.standardMegaMounted = 'true';
    });

    collect(scope, '[data-mobile-editorial-mega-mount]').forEach((mount) => {
      if (mount.dataset.mobileMegaMounted === 'true') return;

      const heading = mount.dataset.standardMegaHeading;
      const header = mount.closest('[data-header-section-id]') || document;
      const source = collect(header, '[data-standard-mega-source]').find((candidate) => (
        candidate.dataset.standardMegaHeading === heading
      ));

      if (!heading || !source) return;

      const screen = mount.closest('[data-drawer-screen]');
      const fallbackNavigation = screen?.querySelector(':scope > .theme-header__drawer-nav');
      const isEditorialMenu = source.classList.contains('theme-header__mega-block--editorial');
      const isStandardMenu = source.classList.contains('theme-header__mega-block--standard');
      const mobileMenu = cloneStandardMegaMenuSource(source);

      if (isStandardMenu) {
        mobileMenu.querySelector(':scope > .theme-header__standard-mega')
          ?.classList.add('theme-header__standard-mega--mobile-images-only');
        Array.from(mobileMenu.classList)
          .filter((className) => className.startsWith('color-'))
          .forEach((className) => mobileMenu.classList.remove(className));
        mobileMenu.classList.add('color-scheme-1');

        const replacementHandles = new Set([
          ...collect(source, 'template[data-standard-mega-override]')
            .filter((template) => template.content?.querySelector('[data-standard-mega-override-column]'))
            .map((template) => template.dataset.targetHandle || ''),
          ...collect(source, '.theme-header__standard-mega-linked-card')
            .map((card) => card.dataset.standardMegaLinkedHandle || ''),
        ].filter(Boolean));

        replacementHandles.forEach((handle) => {
          screen?.querySelector(
            `[data-mobile-menu-item-handle="${CSS.escape(handle)}"]`
          )?.remove();
        });
      } else {
        fallbackNavigation?.remove();
        screen?.classList.toggle('theme-header__drawer-screen--editorial', isEditorialMenu);

        if (screen) {
          Array.from(screen.classList)
            .filter((className) => className.startsWith('color-'))
            .forEach((className) => screen.classList.remove(className));
          screen.classList.add('color-scheme-1');
        }
      }

      mount.replaceChildren(mobileMenu);
      mount.hidden = false;
      mount.dataset.mobileMegaMounted = 'true';
    });

    collect(scope, '[data-standard-mega-menu]').forEach((menu) => {
      if (menu.dataset.standardMegaInitialized === 'true') return;
      menu.dataset.standardMegaInitialized = 'true';

      const imageColumns = menu.querySelector('[data-standard-mega-image-columns]');
      const itemsByHandle = new Map();
      const columnsByHandle = new Map();
      const linkedCardsByHandle = new Map();

      collect(menu, '[data-standard-mega-linked-handle]').forEach((card) => {
        const handle = card.dataset.standardMegaLinkedHandle || '';
        if (handle) linkedCardsByHandle.set(handle, card);
      });

      collect(menu, '[data-standard-mega-item]').forEach((item) => {
        const itemHandle = item.dataset.itemHandle || '';

        if (!itemHandle) return;

        if (!itemsByHandle.has(itemHandle)) {
          itemsByHandle.set(itemHandle, []);
        }
        itemsByHandle.get(itemHandle).push(item);
      });

      collect(menu, '[data-standard-mega-column]').forEach((column) => {
        const columnHandle = column.dataset.columnHandle || '';
        if (columnHandle && !columnsByHandle.has(columnHandle)) {
          columnsByHandle.set(columnHandle, column);
        }
      });

      collect(menu, 'template[data-standard-mega-override]').forEach((template) => {
        const targetHandle = template.dataset.targetHandle || '';
        const linkedCard = linkedCardsByHandle.get(targetHandle);

        if (linkedCard && template.content) {
          const linkedFragment = template.content.cloneNode(true);
          const customImage = linkedFragment.querySelector('.theme-header__standard-mega-image');
          const linkedMedia = linkedCard.querySelector('.theme-header__standard-mega-linked-media');
          const customMedia = linkedFragment.querySelector('.theme-header__standard-mega-media');
          const customDescription = linkedFragment.querySelector('[data-standard-mega-description]');
          const linkedDescription = linkedCard.querySelector('[data-standard-mega-description]');

          if (customImage && linkedMedia) {
            customImage.className = 'theme-header__standard-mega-linked-image';
            linkedMedia.replaceChildren(customImage);
          }

          const customRatioClass = Array.from(customMedia?.classList || [])
            .find((className) => (
              className.startsWith('theme-header__standard-mega-media--')
            ));

          if (customRatioClass && linkedMedia) {
            Array.from(linkedMedia.classList)
              .filter((className) => className.startsWith('theme-header__standard-mega-linked-media--'))
              .forEach((className) => linkedMedia.classList.remove(className));
            linkedMedia.classList.add(customRatioClass.replace(
              'theme-header__standard-mega-media--',
              'theme-header__standard-mega-linked-media--'
            ));
          }

          if (customDescription && linkedDescription) {
            linkedDescription.innerHTML = customDescription.innerHTML;
            linkedDescription.hidden = false;
          }
          return;
        }

        const fallbackColumn = columnsByHandle.get(targetHandle);
        const fallbackItem = itemsByHandle.get(targetHandle)?.[0];
        const fallbackLink = fallbackColumn || fallbackItem;

        if (!targetHandle || !fallbackLink || !template.content || !imageColumns) return;

        const fragment = template.content.cloneNode(true);
        const imageColumn = fragment.querySelector('[data-standard-mega-override-column]');

        if (!imageColumn) return;

        imageColumn.dataset.standardMegaTargetHandle = targetHandle;

        const fallbackUrl = fallbackColumn?.dataset.columnUrl || fallbackItem?.dataset.itemUrl || '#';
        const fallbackTitle = fallbackColumn?.dataset.columnTitle || fallbackItem?.dataset.itemTitle || '';

        collect(imageColumn, '[data-standard-mega-default-link]').forEach((link) => {
          const href = link.getAttribute('href');
          if (!href || href === '#') {
            link.setAttribute('href', fallbackUrl);
          }
        });

        const title = imageColumn.querySelector('[data-standard-mega-title]');
        if (title && !title.textContent.trim()) {
          title.textContent = fallbackTitle;
        }

        const resolvedParentHandle = fallbackItem?.dataset.parentHandle || '';
        if (resolvedParentHandle) {
          imageColumn.dataset.standardMegaParentHandle = resolvedParentHandle;
        }

        fallbackLink.remove();
        imageColumn.hidden = false;
        imageColumns.append(imageColumn);
      });

      collect(menu, 'template[data-editorial-mega-override]').forEach((template) => {
        const targetHandle = template.dataset.targetHandle || '';
        const item = targetHandle
          ? menu.querySelector(`[data-editorial-mega-item="${CSS.escape(targetHandle)}"]`)
          : null;

        if (!item || !template.content) return;

        const fragment = template.content.cloneNode(true);
        const customImage = fragment.querySelector('.theme-header__editorial-mega-item-image');
        const customDescription = fragment.querySelector('[data-editorial-mega-description]');
        const itemMedia = item.querySelector('.theme-header__editorial-mega-item-media');
        const itemDescription = item.querySelector('[data-editorial-mega-item-description]');

        if (customImage && itemMedia) {
          itemMedia.replaceChildren(customImage);
        }

        if (customDescription && itemDescription) {
          itemDescription.innerHTML = customDescription.innerHTML;
          itemDescription.hidden = false;
        }
      });

      const mobileImagesOnly = menu.querySelector(
        ':scope > .theme-header__standard-mega--mobile-images-only'
      );

      if (mobileImagesOnly) {
        const hasImages = Boolean(mobileImagesOnly.querySelector(
          '.theme-header__standard-mega-image-column:not([hidden]), .theme-header__standard-mega-linked-card'
        ));
        const mobileMount = menu.closest('[data-mobile-editorial-mega-mount]');

        if (mobileMount) {
          const screen = mobileMount.closest('[data-drawer-screen]');
          const replacementHandles = new Set([
            ...collect(mobileImagesOnly, '.theme-header__standard-mega-image-column:not([hidden])')
              .map((column) => column.dataset.standardMegaTargetHandle || ''),
            ...collect(mobileImagesOnly, '.theme-header__standard-mega-linked-card')
              .map((card) => card.dataset.standardMegaLinkedHandle || ''),
          ].filter(Boolean));

          replacementHandles.forEach((handle) => {
            screen?.querySelector(
              `[data-mobile-menu-item-handle="${CSS.escape(handle)}"]`
            )?.remove();
          });
        }

        if (mobileMount) mobileMount.hidden = !hasImages;
        return;
      }

      initMobileStandardMegaMenu(menu);
    });

    alignStandardMegaSubmenus(scope);

    if (window.__pegasusStandardMegaResizeInitialized !== true) {
      window.__pegasusStandardMegaResizeInitialized = true;
      window.addEventListener('resize', () => {
        window.requestAnimationFrame(() => {
          alignStandardMegaSubmenus(document);
        });
      }, { passive: true });
    }
  }

  function updateDrawerStackHeight(stack) {
    if (!stack) return;

    const activeScreen = Array.from(stack.querySelectorAll('[data-drawer-screen]')).find((screen) => (
      screen.dataset.drawerState === 'active'
    ));

    if (!activeScreen) return;

    const panel = stack.closest('.theme-header__drawer-panel');
    const panelRect = panel?.getBoundingClientRect();
    const stackRect = stack.getBoundingClientRect();
    const availableHeight = panelRect
      ? Math.max(0, panelRect.bottom - stackRect.top)
      : window.innerHeight;

    const nextHeight = Math.ceil(availableHeight);

    stack.style.height = `${nextHeight}px`;
    activeScreen.style.height = `${nextHeight}px`;
  }

  function syncDrawerStacks(scope = document) {
    collect(scope, '[data-drawer-stack]').forEach((stack) => {
      updateDrawerStackHeight(stack);
    });
  }

  function setActiveDrawerScreen(stack, targetId = 'root') {
    if (!stack) return;

    const screens = Array.from(stack.querySelectorAll('[data-drawer-screen]'));
    const target = screens.find((screen) => screen.dataset.drawerScreen === targetId) || screens[0];

    if (!target) return;

    const targetPath = String(target.dataset.drawerPath || 'root');

    collect(target, 'img[data-deferred-image]').forEach((image) => {
      image.loading = 'eager';
      hydrateDeferredImage(image);
    });

    screens.forEach((screen) => {
      const screenPath = String(screen.dataset.drawerPath || 'root');
      let state = 'future';

      if (screen === target) {
        state = 'active';
      } else if (screenPath === 'root' && targetPath !== 'root') {
        state = 'history';
      } else if (screenPath !== 'root' && targetPath.startsWith(`${screenPath}.`)) {
        state = 'history';
      }

      screen.dataset.drawerState = state;
      screen.setAttribute('aria-hidden', state === 'active' ? 'false' : 'true');
    });

    window.requestAnimationFrame(() => {
      updateDrawerStackHeight(stack);
    });
  }

  function setMobileMenuAccordion(trigger, panel, isOpen, { animate = true } = {}) {
    if (!trigger || !panel) return;

    window.clearTimeout(panel.__drawerAccordionTimer);
    window.cancelAnimationFrame(panel.__drawerAccordionFrame);
    panel.__drawerAccordionTimer = null;
    panel.__drawerAccordionFrame = null;
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    panel.toggleAttribute('inert', !isOpen);

    if (isOpen) {
      panel.hidden = false;
      panel.classList.remove('is-open');

      if (!animate || reducedMotionMedia.matches) {
        panel.classList.add('is-open');
        return;
      }

      panel.getBoundingClientRect();
      panel.__drawerAccordionFrame = window.requestAnimationFrame(() => {
        if (trigger.getAttribute('aria-expanded') !== 'true') return;
        panel.classList.add('is-open');
        panel.__drawerAccordionFrame = null;
      });
      return;
    }

    panel.classList.remove('is-open');

    if (!animate || reducedMotionMedia.matches || panel.hidden) {
      panel.hidden = true;
      return;
    }

    panel.__drawerAccordionTimer = window.setTimeout(() => {
      if (trigger.getAttribute('aria-expanded') === 'true') return;
      panel.hidden = true;
      panel.__drawerAccordionTimer = null;
    }, 300);
  }

  function resetDrawerStack(stack) {
    collect(stack, '[data-mobile-menu-accordion-toggle]').forEach((trigger) => {
      const panelId = String(trigger.getAttribute('aria-controls') || '').trim();
      const panel = panelId
        ? stack.querySelector(`#${CSS.escape(panelId)}`)
        : null;

      if (panel) {
        setMobileMenuAccordion(trigger, panel, false, { animate: false });
      } else {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });

    setActiveDrawerScreen(stack, 'root');
  }

  function initDrawerMenus(scope = document) {
    collect(scope, '[data-drawer-stack]').forEach((stack) => {
      if (stack.dataset.drawerInitialized === 'true') {
        updateDrawerStackHeight(stack);
        return;
      }

      stack.dataset.drawerInitialized = 'true';

      collect(stack, '[data-drawer-target]').forEach((button) => {
        button.addEventListener('click', () => {
          const targetId = String(button.dataset.drawerTarget || '').trim();

          if (!targetId) return;

          setActiveDrawerScreen(stack, targetId);
        });
      });

      collect(stack, '[data-mobile-menu-accordion-toggle]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const screen = trigger.closest('[data-drawer-screen]');
          const panelId = String(trigger.getAttribute('aria-controls') || '').trim();
          const panel = panelId && screen
            ? screen.querySelector(`#${CSS.escape(panelId)}`)
            : null;

          if (!screen || !panel) return;

          const shouldOpen = trigger.getAttribute('aria-expanded') !== 'true';

          collect(screen, '[data-mobile-menu-accordion-toggle]').forEach((otherTrigger) => {
            const otherPanelId = String(otherTrigger.getAttribute('aria-controls') || '').trim();
            const otherPanel = otherPanelId
              ? screen.querySelector(`#${CSS.escape(otherPanelId)}`)
              : null;

            if (otherPanel) {
              setMobileMenuAccordion(otherTrigger, otherPanel, false);
            } else {
              otherTrigger.setAttribute('aria-expanded', 'false');
            }
          });

          if (shouldOpen) {
            setMobileMenuAccordion(trigger, panel, true);
          }

          window.requestAnimationFrame(() => {
            updateDrawerStackHeight(stack);
          });
        });
      });

      window.addEventListener('resize', () => {
        updateDrawerStackHeight(stack);
      });

      setActiveDrawerScreen(stack, 'root');
    });
  }

  function initPredictiveSearch(scope = document) {
    collect(scope, '[data-predictive-search]').forEach((dialog) => {
      if (dialog.dataset.predictiveSearchInitialized === 'true') return;
      dialog.dataset.predictiveSearchInitialized = 'true';

      const dialogId = dialog.id;
      const header = dialog.closest('.theme-header') || document;
      const triggers = collect(header, `[data-predictive-search-open][aria-controls="${dialogId}"]`);
      const closeButtons = collect(dialog, '[data-predictive-search-close]');
      const form = dialog.querySelector('[data-predictive-search-form]');
      const input = dialog.querySelector('[data-predictive-search-input]');
      const results = dialog.querySelector('[data-predictive-search-results]');
      const status = dialog.querySelector('[data-predictive-search-status]');
      const searchUrl = String(dialog.dataset.searchUrl || `${getLocaleRoot()}search`).trim();
      const searchFormTypes = String(dialog.dataset.searchFormTypes || 'product,page').trim();
      const searchTypes = String(dialog.dataset.predictiveSearchTypes || 'product,collection,page,article,query').trim();
      const searchLimit = Number(dialog.dataset.predictiveSearchLimit || 6);
      const defaultMarkup = String(results?.innerHTML || '');
      let searchTimer = null;
      let searchController = null;
      let searchRequestId = 0;

      function resetSearchUi() {
        if (results) {
          results.innerHTML = defaultMarkup;
        }

        if (status) {
          status.textContent = '';
        }
      }

      function closeSearch() {
        window.clearTimeout(searchTimer);

        if (searchController) {
          searchController.abort();
          searchController = null;
        }

        closeDialog(dialog);
      }

      async function runSearch() {
        const query = String(input?.value || '').trim();

        if (!results) return;

        if (searchController) {
          searchController.abort();
          searchController = null;
        }

        if (query.length < 2) {
          resetSearchUi();
          return;
        }

        const requestId = searchRequestId + 1;
        searchRequestId = requestId;
        searchController = new AbortController();

        if (status) {
          status.textContent = 'Searching...';
        }

        try {
          const data = await loadPredictiveSearchResults({
            query,
            limit: searchLimit,
            signal: searchController.signal,
            types: searchTypes,
          });

          if (requestId !== searchRequestId) return;

          results.innerHTML = renderPredictiveSearchResults(data, {
            query,
            searchFormTypes,
            searchUrl,
          });

          if (status) {
            status.textContent = '';
          }
        } catch (error) {
          if (error?.name === 'AbortError' || requestId !== searchRequestId) {
            return;
          }

          results.innerHTML = `<div class="theme-header__predictive-empty"><p class="theme-header__predictive-copy">${escapeHtml(error?.error || 'Unable to search right now.')}</p></div>`;

          if (status) {
            status.textContent = '';
          }
        } finally {
          if (requestId === searchRequestId) {
            searchController = null;
          }
        }
      }

      function scheduleSearch(delay = 160) {
        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
          runSearch();
        }, delay);
      }

      function openSearch() {
        openDialog(dialog);

        window.requestAnimationFrame(() => {
          if (!input) return;

          input.focus();

          if (String(input.value || '').trim().length >= 2) {
            scheduleSearch(0);
          } else {
            resetSearchUi();
          }
        });
      }

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          openSearch();
        });
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          closeSearch();
        });
      });

      dialog.addEventListener('close', () => {
        resetSearchUi();
      });

      if (input) {
        input.addEventListener('input', () => {
          scheduleSearch();
        });
      }

      if (form) {
        form.addEventListener('submit', () => {
          closeSearch();
        });
      }
    });
  }

  function initQuickAdd(scope = document) {
    function resetQuickAdd(rootNode) {
      const section = rootNode ? rootNode.querySelector('[data-product-section-id]') : null;

      if (section) {
        section.dispatchEvent(new CustomEvent('pegasus:quick-add-reset'));
      }
    }

    function setQuickAddOpen(rootNode, isOpen) {
      if (!rootNode) return;

      rootNode.dataset.open = isOpen ? 'true' : 'false';

      const trigger = rootNode.querySelector('[data-quick-add-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      if (!isOpen) {
        resetQuickAdd(rootNode);
      }
    }

    collect(scope, '[data-quick-add]').forEach((quickAdd) => {
      if (quickAdd.dataset.quickAddInitialized === 'true') return;
      quickAdd.dataset.quickAddInitialized = 'true';

      const supportsHoverQuickAdd = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const usesMobileNativeQuickAdd = quickAdd.dataset.quickAddMobileNativeEnabled === 'true' && !supportsHoverQuickAdd;
      const trigger = quickAdd.querySelector('[data-quick-add-trigger]');
      const panel = quickAdd.querySelector('[data-quick-add-panel]');
      const shield = quickAdd.querySelector('[data-quick-add-shield]');
      const productCard = quickAdd.closest('.product-card');
      const optionShells = collect(quickAdd, '[data-quick-add-options-shell]');

      optionShells.forEach((optionShell) => {
        const values = optionShell.querySelector('.theme-product-options__values');
        const previousButton = optionShell.querySelector('[data-quick-add-scroll-previous]');
        const nextButton = optionShell.querySelector('[data-quick-add-scroll-next]');

        if (!values || !previousButton || !nextButton) return;

        const updateOverflowState = () => {
          const maxScrollLeft = Math.max(0, values.scrollWidth - values.clientWidth);
          const isOverflowing = maxScrollLeft > 1;
          const canScrollPrevious = isOverflowing && values.scrollLeft > 1;
          const canScrollNext = isOverflowing && values.scrollLeft < maxScrollLeft - 1;

          optionShell.classList.toggle('is-overflowing', isOverflowing);
          previousButton.hidden = !canScrollPrevious;
          previousButton.disabled = !canScrollPrevious;
          nextButton.hidden = !canScrollNext;
          nextButton.disabled = !canScrollNext;
        };

        const scrollOptions = (direction) => {
          values.scrollBy({
            behavior: 'smooth',
            left: direction * Math.max(96, values.clientWidth * 0.72),
          });
        };

        previousButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollOptions(-1);
        });

        nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollOptions(1);
        });

        values.addEventListener('scroll', updateOverflowState, { passive: true });

        if (typeof ResizeObserver === 'function') {
          const overflowObserver = new ResizeObserver(updateOverflowState);
          overflowObserver.observe(values);
        }

        window.requestAnimationFrame(updateOverflowState);
      });

      if (usesMobileNativeQuickAdd) {
        return;
      }

      quickAdd.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      }, { capture: true });

      quickAdd.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      if (!trigger || !panel) return;

      if (shield) {
        shield.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          event.stopPropagation();
        });

        shield.addEventListener('click', (event) => {
          if (trigger.disabled) return;

          event.preventDefault();
          event.stopPropagation();
          setQuickAddOpen(quickAdd, true);
        });
      }

      if (supportsHoverQuickAdd) {
        trigger.addEventListener('mouseenter', () => {
          if (trigger.disabled) return;
          setQuickAddOpen(quickAdd, true);
        });
      }

      trigger.addEventListener('click', (event) => {
        if (trigger.disabled) return;

        event.preventDefault();
        event.stopPropagation();
        setQuickAddOpen(quickAdd, quickAdd.dataset.open !== 'true');
      });

      if (productCard && supportsHoverQuickAdd) {
        productCard.addEventListener('mouseleave', () => {
          setQuickAddOpen(quickAdd, false);
        });
      }

      quickAdd.addEventListener('focusin', () => {
        if (trigger.disabled || usesMobileNativeQuickAdd) return;
        setQuickAddOpen(quickAdd, true);
      });

      quickAdd.addEventListener('focusout', () => {
        if (usesMobileNativeQuickAdd) return;

        window.setTimeout(() => {
          if (quickAdd.contains(document.activeElement)) return;
          if (supportsHoverQuickAdd && quickAdd.matches(':hover')) return;
          setQuickAddOpen(quickAdd, false);
        }, 0);
      });

      quickAdd.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        setQuickAddOpen(quickAdd, false);
        trigger.focus();
      });
    });

    if (document.body.dataset.quickAddDocumentBound !== 'true') {
      document.body.dataset.quickAddDocumentBound = 'true';

      document.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest('[data-quick-add-trigger]');

        if (!trigger || trigger.disabled) return;

        const quickAdd = trigger.closest('[data-quick-add]');
        if (!quickAdd) return;

        event.preventDefault();
        event.stopPropagation();
        setQuickAddOpen(quickAdd, quickAdd.dataset.open !== 'true');
      }, true);

      document.addEventListener('click', (event) => {
        collect(document, '[data-quick-add][data-open="true"]').forEach((quickAdd) => {
          if (quickAdd.contains(event.target)) return;
          setQuickAddOpen(quickAdd, false);
        });
      });
    }
  }

  function initCartDrawer(scope = document) {
    collect(scope, '[data-cart-change-line]').forEach((button) => {
      if (button.dataset.cartChangeInitialized === 'true') return;
      button.dataset.cartChangeInitialized = 'true';

      button.addEventListener('click', async () => {
        const line = Number(button.dataset.cartChangeLine);
        const quantity = Number(button.dataset.cartChangeQuantity);

        if (!line || Number.isNaN(quantity) || quantity < 0) return;

        button.disabled = true;

        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ line, quantity }),
          });

          const cart = await response.json();

          if (!response.ok) {
            throw new Error(cart.description || 'Unable to update cart.');
          }

          updateCartCount(cart.item_count);
          await refreshHeaderCartState({ openDrawer: true });
        } catch (error) {
          announce(error.message || 'Unable to update cart.');
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  function initAnimations(scope = document) {
    const animationStyle = body.dataset.animationStyle;
    const animatedNodes = collect(scope, '[data-animate]');

    if (animatedNodes.length === 0) return;

    const mainContent = document.getElementById('MainContent');
    const firstPageSection = mainContent?.querySelector(':scope > .shopify-section:first-child');
    const getAnimationTargets = (node) => {
      const sectionWrapper = node.closest('.shopify-section');
      const decorations = sectionWrapper?.parentElement === mainContent
        ? [...sectionWrapper.querySelectorAll(':scope > [data-animate-decoration]')]
        : [];

      return [node, ...decorations];
    };
    const showAnimationTargets = (node) => {
      getAnimationTargets(node).forEach((target) => {
        target.classList.add('is-visible');
        target.style.removeProperty('opacity');
        target.style.removeProperty('transform');
        target.style.removeProperty('visibility');
        target.style.removeProperty('will-change');
      });
    };
    const staticNodes = animatedNodes.filter((node) => (
      !mainContent?.contains(node) || firstPageSection?.contains(node)
    ));
    const revealableNodes = animatedNodes.filter((node) => !staticNodes.includes(node));

    staticNodes.forEach((node) => {
      const targets = getAnimationTargets(node);

      animationObserver?.unobserve(node);
      window.gsap?.killTweensOf(targets);
      node.dataset.animateInitialized = 'true';
      node.dataset.animationRevealed = 'true';
      node.dataset.animateStatic = 'true';
      showAnimationTargets(node);
    });

    if (!animationStyle) {
      revealableNodes.forEach(showAnimationTargets);
      return;
    }

    const revealNode = (node) => {
      if (node.dataset.animationRevealed === 'true') return;

      node.dataset.animationRevealed = 'true';
      const targets = getAnimationTargets(node);
      targets.forEach((target) => target.classList.add('is-visible'));

      if (reducedMotionMedia.matches || typeof window.gsap === 'undefined') {
        showAnimationTargets(node);
        return;
      }

      const duration = Number.parseFloat(body.dataset.animationDuration) || 0.5;
      const translateY = animationStyle === 'fade-up'
        ? 32
        : (animationStyle === 'fade-down' ? -32 : 0);

      window.gsap.fromTo(
        targets,
        {
          autoAlpha: 0,
          willChange: 'opacity, transform',
          y: translateY,
        },
        {
          autoAlpha: 1,
          clearProps: 'opacity,transform,visibility,willChange',
          duration,
          ease: 'power2.out',
          overwrite: 'auto',
          y: 0,
        }
      );
    };

    if (!animationObserver) {
      animationObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            revealNode(entry.target);
            animationObserver.unobserve(entry.target);
          });
        },
        {
          rootMargin: mobileViewportMedia.matches ? '0px 0px 30% 0px' : '0px 0px -10% 0px',
          threshold: mobileViewportMedia.matches ? 0.01 : 0.12,
        }
      );
    }

    revealableNodes.forEach((node) => {
      if (node.dataset.animateInitialized === 'true') return;
      node.dataset.animateInitialized = 'true';

      const bounds = node.getBoundingClientRect();
      const isAlreadyVisible = (
        node.hasAttribute('data-animate-immediate')
        || (bounds.top < window.innerHeight && bounds.bottom > 0)
      );

      if (isAlreadyVisible) {
        revealNode(node);
        return;
      }

      animationObserver.observe(node);
    });
  }

  function initSmoothScroll() {
    if (body.dataset.smoothScrollDesktop !== 'true') return;

    if (!smoothScrollListenersBound) {
      smoothScrollListenersBound = true;
      desktopSmoothMedia.addEventListener('change', initSmoothScroll);
      reducedMotionMedia.addEventListener('change', initSmoothScroll);
    }

    const shouldSmooth = desktopSmoothMedia.matches && !reducedMotionMedia.matches;

    if (!shouldSmooth) {
      if (scrollSmoother) {
        scrollSmoother.kill();
        scrollSmoother = null;
      }
      return;
    }

    if (
      scrollSmoother
      || typeof window.gsap === 'undefined'
      || typeof window.ScrollTrigger === 'undefined'
      || typeof window.ScrollSmoother === 'undefined'
    ) {
      return;
    }

    const wrapper = document.querySelector('[data-theme-smooth-wrapper]');
    const content = document.querySelector('[data-theme-smooth-content]');
    const headerSpacer = content?.querySelector('[data-theme-smooth-header-spacer]');

    if (!wrapper || !content) return;

    const syncHeaderSpacer = () => {
      if (!headerSpacer) return false;

      let siteHeaderHeight = 0;
      const headerHeight = [...document.querySelectorAll('.shopify-section-group-header-group')]
        .reduce((height, section) => {
          const styles = window.getComputedStyle(section);
          const marginTop = Number.parseFloat(styles.marginTop) || 0;
          const marginBottom = Number.parseFloat(styles.marginBottom) || 0;
          const isAnnouncementSection = Boolean(section.querySelector('.announcement-bar'));
          const storedAnnouncementHeight = Number.parseFloat(
            root.style.getPropertyValue('--theme-announcement-bar-height'),
          ) || 0;
          const sectionHeight = isAnnouncementSection
            ? Math.max(section.scrollHeight, storedAnnouncementHeight)
            : section.offsetHeight;
          const sectionLayoutHeight = sectionHeight + marginTop + marginBottom;

          if (!isAnnouncementSection) {
            siteHeaderHeight += sectionLayoutHeight;
          }

          return height + sectionLayoutHeight;
        }, 0);

      const previousSiteHeaderHeight = Number.parseFloat(
        content.style.getPropertyValue('--theme-smooth-site-header-height')
      ) || 0;
      const layoutChanged = Math.abs(headerHeight - smoothHeaderLayoutHeight) >= 0.5;
      const siteHeaderChanged = Math.abs(siteHeaderHeight - previousSiteHeaderHeight) >= 0.5;

      if (!layoutChanged && !siteHeaderChanged) return false;

      smoothHeaderLayoutHeight = headerHeight;
      content.style.setProperty('--theme-smooth-header-height', `${headerHeight}px`);
      content.style.setProperty('--theme-smooth-site-header-height', `${siteHeaderHeight}px`);
      return true;
    };

    syncHeaderSpacer();

    if (!smoothHeaderResizeObserver && typeof ResizeObserver === 'function') {
      smoothHeaderResizeObserver = new ResizeObserver(() => {
        if (syncHeaderSpacer()) window.ScrollTrigger?.refresh();
      });

      document.querySelectorAll('.shopify-section-group-header-group:not(:has(.announcement-bar))').forEach((section) => {
        smoothHeaderResizeObserver.observe(section);
      });
    }

    window.gsap.registerPlugin(window.ScrollTrigger, window.ScrollSmoother);
    scrollSmoother = window.ScrollSmoother.create({
      content,
      effects: false,
      onUpdate: () => {
        smoothScrollUpdateCallbacks.forEach((callback) => callback());
      },
      smooth: 0.82,
      smoothTouch: false,
      wrapper,
    });

    if (!smoothScrollAnchorHandlerBound) {
      smoothScrollAnchorHandlerBound = true;

      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');

        if (!scrollSmoother || !link) return;

        const linkUrl = new URL(link.href, window.location.href);

        if (
          linkUrl.origin !== window.location.origin
          || linkUrl.pathname !== window.location.pathname
          || linkUrl.search !== window.location.search
          || !linkUrl.hash
        ) {
          return;
        }

        const targetId = decodeURIComponent(linkUrl.hash.slice(1));
        const target = document.getElementById(targetId);

        if (!target) return;

        event.preventDefault();
        scrollSmoother.scrollTo(target, true);
        window.history.replaceState(null, '', linkUrl.hash);
      });
    }

    if (window.location.hash) {
      const targetId = decodeURIComponent(window.location.hash.slice(1));
      const target = document.getElementById(targetId);

      if (target) {
        scrollSmoother.scrollTo(target, false);
      }
    }

    window.ScrollTrigger.refresh();

  }

  function initFullWidthScrollCards(scope = document) {
    collect(scope, '[data-scroll-story]').forEach((section) => {
      if (section.dataset.scrollStoryInitialized === 'true') return;

      section.dataset.scrollStoryInitialized = 'true';

      const cards = [...section.querySelectorAll('[data-scroll-story-card]')];
      const layoutMedia = window.matchMedia('(min-width: 768px)');
      const controller = new AbortController();
      let scrollTriggers = [];
      let gsapTweens = [];
      let storyOffset = 0;
      let viewportWidth = window.innerWidth;
      let resizeFrame;

      const getStickyOffset = () => {
        const configuredValue = window.getComputedStyle(root).getPropertyValue('--theme-sticky-header-height');
        const configuredOffset = Number.parseFloat(configuredValue) || 0;

        // The announcement bar scrolls out before this story reaches its pin
        // point. Including its original height leaves an empty gap above every
        // pinned card, so only the persistent sticky header belongs here.
        if (configuredOffset > 0) return configuredOffset;

        const stickyHeader = document.querySelector('.theme-header-section--sticky');

        if (!stickyHeader) return 0;

        const styles = window.getComputedStyle(stickyHeader);
        const bounds = stickyHeader.getBoundingClientRect();
        const isPinnedHeader = styles.position === 'sticky' || styles.position === 'fixed';

        return isPinnedHeader ? Math.max(0, bounds.height) : 0;
      };

      const destroyStory = () => {
        scrollTriggers.forEach((trigger) => trigger.kill(true));
        gsapTweens.forEach((tween) => tween.kill());
        scrollTriggers = [];
        gsapTweens = [];

        section.classList.remove('is-scroll-story-enhanced', 'is-scroll-story-css-sticky');
        cards.forEach((card) => {
          const inner = card.querySelector('.theme-full-width-scroll-cards__inner');
          const visual = card.querySelector('.theme-full-width-scroll-cards__visual');

          window.gsap?.set([inner, visual].filter(Boolean), { clearProps: 'transform,opacity,willChange' });
        });
      };

      const buildStory = () => {
        destroyStory();

        if (cards.length === 0) return;

        // Keep the pin position stable while mobile browser chrome and the
        // announcement bar change height. ScrollTrigger refreshes on those
        // height-only changes; recalculating the offset during that refresh
        // makes pinned cards visibly jump.
        storyOffset = Math.round(getStickyOffset());
        section.style.setProperty('--scroll-story-sticky-offset', `${storyOffset}px`);

        if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
          section.classList.add('is-scroll-story-css-sticky');
          return;
        }

        section.classList.add('is-scroll-story-enhanced');

        cards.forEach((card, index) => {
          const nextCard = cards[index + 1];
          const inner = card.querySelector('.theme-full-width-scroll-cards__inner');
          const visual = card.querySelector('.theme-full-width-scroll-cards__visual');
          const pinPosition = layoutMedia.matches ? `top top+=${storyOffset}` : 'bottom bottom';

          if (nextCard) {
            scrollTriggers.push(window.ScrollTrigger.create({
              anticipatePin: 1,
              end: pinPosition,
              endTrigger: nextCard,
              invalidateOnRefresh: true,
              pin: card,
              pinSpacing: false,
              start: pinPosition,
              trigger: card,
            }));

            if (!reducedMotionMedia.matches && inner) {
              const cardTween = window.gsap.to(inner, {
                ease: 'none',
                opacity: 0.88,
                scale: layoutMedia.matches ? 0.975 : 0.99,
                scrollTrigger: {
                  end: () => `top top+=${storyOffset}`,
                  endTrigger: nextCard,
                  invalidateOnRefresh: true,
                  scrub: layoutMedia.matches ? 0.45 : 0.25,
                  start: 'top bottom',
                  trigger: nextCard,
                },
                transformOrigin: layoutMedia.matches ? 'center top' : 'center bottom',
                y: layoutMedia.matches ? -18 : -8,
              });

              gsapTweens.push(cardTween);
              scrollTriggers.push(cardTween.scrollTrigger);
            }
          }

          if (!reducedMotionMedia.matches && visual) {
            const visualTween = window.gsap.fromTo(
              visual,
              { scale: layoutMedia.matches ? 1.06 : 1.03, yPercent: layoutMedia.matches ? -2 : -1 },
              {
                ease: 'none',
                scale: layoutMedia.matches ? 1.06 : 1.03,
                scrollTrigger: {
                  end: 'bottom top',
                  invalidateOnRefresh: true,
                  scrub: layoutMedia.matches ? 0.5 : 0.25,
                  start: 'top bottom',
                  trigger: card,
                },
                yPercent: layoutMedia.matches ? 2 : 1,
              }
            );

            gsapTweens.push(visualTween);
            scrollTriggers.push(visualTween.scrollTrigger);
          }
        });

        window.requestAnimationFrame(() => window.ScrollTrigger.refresh());
      };

      layoutMedia.addEventListener('change', buildStory, { signal: controller.signal });
      reducedMotionMedia.addEventListener('change', buildStory, { signal: controller.signal });
      window.addEventListener('resize', () => {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
          const nextWidth = window.innerWidth;

          // Mobile address bars resize viewport height while scrolling. Rebuild
          // only when the available width actually changes.
          if (Math.abs(nextWidth - viewportWidth) < 2) return;

          viewportWidth = nextWidth;
          buildStory();
        });
      }, { signal: controller.signal, passive: true });

      registerCleanup(section, () => {
        controller.abort();
        window.cancelAnimationFrame(resizeFrame);
        destroyStory();
        section.style.removeProperty('--scroll-story-sticky-offset');
        delete section.dataset.scrollStoryInitialized;
      });

      buildStory();
    });
  }

  let imageRevealObserver = null;
  let imageRevealMutationObserver = null;
  const imageRevealStates = new WeakMap();

  function getImageRevealSurface(image) {
    if (!(image instanceof HTMLImageElement)) return null;

    const responsiveAsset = image.closest('.theme-responsive-media__asset');

    if (
      image.classList.contains('product-card__image--secondary')
      || image.classList.contains('theme-product-media__image')
      || image.classList.contains('theme-header__logo')
      || image.classList.contains('theme-header__logo-svg')
      || image.classList.contains('theme-header__drawer-logo-image')
      || (responsiveAsset && window.getComputedStyle(responsiveAsset).display === 'none')
      || image.closest('[data-media-fade]')
    ) {
      return null;
    }

    return image.closest('.product-card')
      || image.closest('.theme-image-card')
      || image;
  }

  function revealImageState(state) {
    if (!state || state.revealed || !state.loaded || !state.inView) return;

    state.revealed = true;

    const animatedAncestorOpacity = state.animatedAncestor
      ? Number.parseFloat(window.getComputedStyle(state.animatedAncestor).opacity)
      : 1;
    const ancestorIsHandlingReveal = (
      state.animatedAncestor
      && Number.isFinite(animatedAncestorOpacity)
      && animatedAncestorOpacity < 0.99
    );

    if (ancestorIsHandlingReveal) {
      state.surface.classList.add('is-image-revealed', 'is-image-revealed--with-ancestor');
      imageRevealObserver?.unobserve(state.surface);
      return;
    }

    window.requestAnimationFrame(() => {
      state.surface.classList.add('is-image-revealed');
    });
    imageRevealObserver?.unobserve(state.surface);
  }

  function prepareImageReveal(image) {
    if (!(image instanceof HTMLImageElement)) return;
    if (image.hasAttribute('data-deferred-image') && image.dataset.deferredImageLoaded !== 'true') return;

    const surface = getImageRevealSurface(image);

    if (!surface || surface.dataset.imageRevealInitialized === 'true') return;

    surface.dataset.imageRevealInitialized = 'true';
    surface.setAttribute('data-image-reveal', '');

    const state = {
      animatedAncestor: surface.closest('[data-animate]'),
      image,
      inView: false,
      loaded: image.complete,
      revealed: false,
      surface,
    };

    imageRevealStates.set(surface, state);
    registerCleanup(surface, () => {
      imageRevealObserver?.unobserve(surface);
      imageRevealStates.delete(surface);
    });

    const markLoaded = () => {
      state.loaded = true;
      revealImageState(state);
    };

    if (!image.complete) {
      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener('error', markLoaded, { once: true });
    }

    if (typeof IntersectionObserver !== 'function') {
      state.inView = true;
      revealImageState(state);
      return;
    }

    if (!imageRevealObserver) {
      imageRevealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const entryState = imageRevealStates.get(entry.target);

            if (!entryState || !entry.isIntersecting) return;

            if (entryState.image.loading === 'lazy') {
              entryState.image.loading = 'eager';
            }
            entryState.inView = true;
            revealImageState(entryState);
          });
        },
        {
          rootMargin: mobileViewportMedia.matches ? '700px 0px' : '240px 0px',
          threshold: 0.01,
        }
      );
    }

    imageRevealObserver.observe(surface);
  }

  function initImageReveals(scope = document) {
    collect(scope, 'img').forEach((image) => {
      prepareImageReveal(image);
    });

    if (!imageRevealMutationObserver && document.body && typeof MutationObserver === 'function') {
      imageRevealMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            collect(node, 'img').forEach((image) => {
              prepareImageReveal(image);
            });
          });
        });
      });
      imageRevealMutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  function initMediaFades(scope = document) {
    collect(scope, '[data-media-fade]').forEach((surface) => {
      if (surface.dataset.mediaFadeInitialized === 'true') return;
      surface.dataset.mediaFadeInitialized = 'true';

      const candidates = Array.from(surface.querySelectorAll('img, video'));
      const visibleMedia = candidates.find((media) => {
        const asset = media.closest('.theme-responsive-media__asset');
        return !asset || window.getComputedStyle(asset).display !== 'none';
      }) || candidates[0];

      const reveal = () => {
        window.requestAnimationFrame(() => {
          surface.classList.add('is-media-loaded');
        });
      };

      if (!visibleMedia) {
        reveal();
        return;
      }

      if (visibleMedia.tagName === 'IMG') {
        if (visibleMedia.complete) {
          reveal();
        } else {
          visibleMedia.addEventListener('load', reveal, { once: true });
          visibleMedia.addEventListener('error', reveal, { once: true });
        }
        return;
      }

      if (visibleMedia.readyState >= 2) {
        reveal();
      } else {
        visibleMedia.addEventListener('loadeddata', reveal, { once: true });
        visibleMedia.addEventListener('error', reveal, { once: true });
      }
    });
  }

  let deferredImageObserver = null;
  let deferredImageVisibilityObserver = null;
  let deferredImageVisibilityFrame = null;

  function isDeferredImageVisible(image) {
    if (!(image instanceof HTMLImageElement) || image.hidden || image.getClientRects().length === 0) return false;

    const styles = window.getComputedStyle(image);

    return styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  function hydrateDeferredImage(image) {
    if (!(image instanceof HTMLImageElement) || image.dataset.deferredImageLoaded === 'true') return;

    const source = String(image.dataset.deferredSrc || '').trim();
    const sourceSet = String(image.dataset.deferredSrcset || '').trim();

    if (sourceSet) image.srcset = sourceSet;
    if (source) image.src = source;

    image.dataset.deferredImageLoaded = 'true';
    image.removeAttribute('data-deferred-src');
    image.removeAttribute('data-deferred-srcset');
    deferredImageObserver?.unobserve(image);
    prepareImageReveal(image);
  }

  function hydrateVisibleDeferredImages(scope = document) {
    collect(scope, 'img[data-deferred-image]').forEach((image) => {
      if (isDeferredImageVisible(image)) hydrateDeferredImage(image);
    });
  }

  function initDeferredImages(scope = document) {
    const images = collect(scope, 'img[data-deferred-image]');

    if (images.length === 0) return;

    if (typeof IntersectionObserver !== 'function') {
      hydrateVisibleDeferredImages(scope);
      return;
    }

    if (!deferredImageObserver) {
      deferredImageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && isDeferredImageVisible(entry.target)) {
            hydrateDeferredImage(entry.target);
          }
        });
      }, {
        rootMargin: mobileViewportMedia.matches ? '700px' : '240px',
      });
    }

    images.forEach((image) => {
      deferredImageObserver.observe(image);
      registerCleanup(image, () => deferredImageObserver?.unobserve(image));
    });

    if (!deferredImageVisibilityObserver && typeof MutationObserver === 'function') {
      const header = document.querySelector('[data-header-section-id]');

      if (header) {
        deferredImageVisibilityObserver = new MutationObserver(() => {
          window.cancelAnimationFrame(deferredImageVisibilityFrame);
          deferredImageVisibilityFrame = window.requestAnimationFrame(() => {
            hydrateVisibleDeferredImages(header);
          });
        });
        deferredImageVisibilityObserver.observe(header, {
          attributeFilter: ['aria-hidden', 'class', 'data-nav-open', 'data-open', 'hidden'],
          attributes: true,
          subtree: true,
        });
      }
    }
  }

  function initStickyHeaders(scope = document) {
    collect(scope, '[data-sticky-mode]').forEach((header) => {
      if (header.dataset.stickyInitialized === 'true') return;
      header.dataset.stickyInitialized = 'true';

      const wrapper = header.closest('.shopify-section');
      const stickyMode = String(header.dataset.stickyMode || 'none');
      const isTransparent = header.classList.contains('theme-header--transparent');
      const announcementSection = document.querySelector('.announcement-bar')
        ?.closest('.shopify-section-group-header-group');
      const announcementBar = announcementSection?.querySelector('.announcement-bar');
      const usesScrubbedAnnouncement = Boolean(
        announcementSection
        && root.classList.contains('theme-smooth-scroll-desktop')
        && desktopSmoothMedia.matches
      );
      let previousY = window.scrollY;
      let stickyStart = 0;
      let headerHidden = false;
      let headerHeight = 0;
      let announcementHeight = 0;

      if (wrapper) {
        wrapper.classList.add('theme-header-section');
        wrapper.classList.toggle('theme-header-section--sticky', stickyMode !== 'none');
        wrapper.classList.toggle('theme-header-section--overlay', isTransparent);
      }

      const scrubAnnouncement = (currentY = window.scrollY) => {
        if (!usesScrubbedAnnouncement || announcementHeight <= 0) return false;

        const visibleHeight = Math.max(0, announcementHeight - Math.max(0, currentY));
        const scrollOffset = announcementHeight - visibleHeight;
        const isCollapsed = visibleHeight <= 0.5;

        root.style.setProperty('--theme-announcement-visible-height', `${visibleHeight}px`);
        root.style.setProperty('--theme-announcement-scroll-offset', `${scrollOffset}px`);
        root.classList.toggle('theme-announcement-collapsed', isCollapsed);

        return isCollapsed;
      };

      const update = () => {
        const currentY = window.scrollY;
        const scrollDelta = currentY - previousY;
        const isAnnouncementCollapsed = scrubAnnouncement(currentY);
        const transparentThreshold = Math.max(4, announcementHeight || stickyStart);
        const isPastStickyStart = isTransparent
          ? isAnnouncementCollapsed || currentY >= transparentThreshold
          : currentY > Math.max(1, stickyStart);

        if (stickyMode === 'scroll_up') {
          if (currentY <= 1 || scrollDelta < -6) {
            headerHidden = false;
          } else if (scrollDelta > 6 && currentY > Math.max(120, stickyStart + 120)) {
            headerHidden = true;
          }
        } else {
          headerHidden = false;
        }

        header.classList.toggle('is-scrolled', isTransparent && isPastStickyStart);
        header.classList.toggle('is-scrolled-down', headerHidden);
        document.documentElement.style.setProperty(
          '--theme-sticky-header-offset',
          `${stickyMode !== 'none' && !headerHidden ? headerHeight : 0}px`,
        );
        previousY = currentY;
      };

      const measure = () => {
        headerHeight = header.getBoundingClientRect().height;

        if (announcementSection) {
          announcementHeight = announcementBar?.scrollHeight || announcementSection.scrollHeight;
          root.style.setProperty('--theme-announcement-bar-height', `${announcementHeight}px`);
        }

        if (wrapper) {
          wrapper.style.setProperty('--theme-header-overlay-height', `${headerHeight}px`);
          stickyStart = wrapper.offsetTop;
        }

        update();
      };

      const finishInitialState = () => {
        header.classList.remove('theme-header--initializing');
      };

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('load', measure, { passive: true, once: true });
      window.addEventListener('pageshow', measure, { passive: true });

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(header);
        header.__pegasusStickyResizeObserver = resizeObserver;
      }

      measure();
      requestAnimationFrame(() => {
        measure();
        requestAnimationFrame(finishInitialState);
      });
      const firstMeasureTimer = window.setTimeout(measure, 120);
      const secondMeasureTimer = window.setTimeout(measure, 320);

      registerCleanup(header, () => {
        window.removeEventListener('scroll', update);
        window.removeEventListener('resize', measure);
        window.removeEventListener('load', measure);
        window.removeEventListener('pageshow', measure);
        window.clearTimeout(firstMeasureTimer);
        window.clearTimeout(secondMeasureTimer);
        root.classList.remove('theme-announcement-collapsed');
        root.style.removeProperty('--theme-announcement-visible-height');
        root.style.removeProperty('--theme-announcement-scroll-offset');
        header.__pegasusStickyResizeObserver?.disconnect();
      });
    });
  }

  function getNewsletterKlaviyoSettings() {
    return {
      enabled: body.dataset.klaviyoNewsletterEnabled === 'true',
      publicKey: String(body.dataset.klaviyoNewsletterPublicKey || '').trim(),
      listId: String(body.dataset.klaviyoNewsletterListId || '').trim(),
    };
  }

  function isNewsletterCustomerForm(form) {
    if (!form || form.dataset.klaviyoNewsletterInitialized === 'true') return false;

    const emailInput = form.querySelector('input[name="contact[email]"]');
    if (!emailInput) return false;

    const tagInput = form.querySelector('input[name="contact[tags]"]');
    const tags = String(tagInput?.value || '').toLowerCase();

    return (
      form.classList.contains('theme-newsletter__form')
      || form.classList.contains('theme-footer__newsletter-form')
      || tags.split(',').map((tag) => tag.trim()).includes('newsletter')
    );
  }

  function buildKlaviyoNewsletterPayload(form, settings) {
    const email = form.querySelector('input[name="contact[email]"]')?.value?.trim();
    const firstName = form.querySelector('input[name="contact[first_name]"]')?.value?.trim();
    const customSource = form.querySelector('[data-klaviyo-source]')?.value?.trim() || 'Shopify theme newsletter';
    const profileAttributes = {
      email,
      subscriptions: {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      },
    };

    if (firstName) {
      profileAttributes.first_name = firstName;
    }

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: settings.listId,
            },
          },
        },
      },
    };
  }

  async function syncKlaviyoNewsletterForm(form, settings) {
    const email = form.querySelector('input[name="contact[email]"]')?.value?.trim();

    if (!settings.enabled || !settings.publicKey || !settings.listId || !email) return;

    const endpoint = 'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(settings.publicKey);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = window.setTimeout(() => {
      controller?.abort();
    }, 3000);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          revision: '2026-04-15',
        },
        body: JSON.stringify(buildKlaviyoNewsletterPayload(form, settings)),
        keepalive: true,
        signal: controller?.signal,
      });

      if (!response.ok) {
        throw new Error('Klaviyo newsletter sync failed with ' + response.status);
      }
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function initKlaviyoNewsletterForms(scope = document) {
    const settings = getNewsletterKlaviyoSettings();

    collect(scope, 'form').forEach((form) => {
      if (!isNewsletterCustomerForm(form)) return;

      form.dataset.klaviyoNewsletterInitialized = 'true';

      form.addEventListener('submit', async (event) => {
        if (form.dataset.klaviyoNewsletterBypass === 'true') return;
        if (!settings.enabled || !settings.publicKey || !settings.listId) return;

        event.preventDefault();

        const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
        if (submitter) submitter.disabled = true;

        try {
          await syncKlaviyoNewsletterForm(form, settings);
        } catch (error) {
          console.warn(error);
        } finally {
          form.dataset.klaviyoNewsletterBypass = 'true';
          HTMLFormElement.prototype.submit.call(form);
        }
      });
    });
  }
  function initAutoSubmit(scope = document) {
    collect(scope, '[data-auto-submit]').forEach((field) => {
      if (field.dataset.autoSubmitInitialized === 'true') return;
      field.dataset.autoSubmitInitialized = 'true';

      field.addEventListener('change', () => {
        if (field.form) field.form.submit();
      });
    });
  }

  function initCustomerAddressForms(scope = document) {
    collect(scope, '[data-address-country]').forEach((countrySelect) => {
      if (countrySelect.dataset.addressInitialized === 'true') return;
      countrySelect.dataset.addressInitialized = 'true';

      const form = countrySelect.closest('form');
      const provinceContainer = form?.querySelector('[data-address-province-container]');
      const provinceSelect = form?.querySelector('[data-address-province]');
      const defaultCountry = String(countrySelect.dataset.default || '').trim();
      const defaultProvince = String(provinceSelect?.dataset.default || '').trim();
      let appliedDefaultProvince = false;

      if (defaultCountry) {
        countrySelect.value = defaultCountry;
      }

      function updateProvinceOptions() {
        if (!provinceContainer || !provinceSelect) return;

        const selectedOption = countrySelect.options[countrySelect.selectedIndex];
        let provinces = [];

        try {
          provinces = JSON.parse(selectedOption?.dataset?.provinces || '[]');
        } catch (error) {
          provinces = [];
        }

        const currentProvinceValue = String(provinceSelect.value || '').trim();

        provinceSelect.innerHTML = provinces.map(([value, label]) => (
          `<option value="${escapeHtml(value)}">${escapeHtml(label)}</option>`
        )).join('');

        provinceContainer.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          provinceSelect.value = '';
          return;
        }

        if (!appliedDefaultProvince && defaultProvince) {
          provinceSelect.value = defaultProvince;
          appliedDefaultProvince = true;
          return;
        }

        if (currentProvinceValue && provinces.some(([value]) => value === currentProvinceValue)) {
          provinceSelect.value = currentProvinceValue;
          return;
        }

        provinceSelect.value = provinces[0][0];
      }

      countrySelect.addEventListener('change', updateProvinceOptions);
      updateProvinceOptions();
    });
  }

  function initAjaxCart(scope = document) {
    if (body.dataset.ajaxCart !== 'true') return;

    const forms = collect(scope, '.theme-product__form, .product-card__form');

    forms.forEach((form) => {
      if (form.dataset.ajaxInitialized === 'true') return;
      form.dataset.ajaxInitialized = 'true';

      form.addEventListener('submit', async (event) => {
        const submitter = event.submitter;

        if (
          submitter
          && (submitter.name === 'checkout' || submitter.closest('.shopify-payment-button'))
        ) {
          return;
        }

        event.preventDefault();

        const button = submitter || form.querySelector('[type="submit"]');
        const buttonLabel = button
          ? button.querySelector('[data-add-to-cart-text], .product-card__quick-add-label')
          : null;
        const originalLabel = buttonLabel
          ? buttonLabel.textContent
          : (button ? button.textContent : '');
        const loadingDrawer = openCartDrawerLoading(form);
        const autoSubmitSection = form.closest('[data-auto-submit-variant="true"]');

        if (button) {
          button.disabled = true;
          if (buttonLabel) {
            buttonLabel.textContent = 'Adding...';
          } else {
            button.textContent = 'Adding...';
          }
        }

        try {
          const response = await fetch('/cart/add.js', {
            body: new FormData(form),
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            method: 'POST',
          });

          const payload = await response.json();

          if (!response.ok) {
            throw new Error(payload.description || 'Unable to add item to cart.');
          }

          const cart = await fetchCart();
          updateCartCount(cart.item_count);
          const parentQuickView = form.closest('[data-quick-view]');
          if (parentQuickView && typeof parentQuickView.close === 'function' && parentQuickView.open) {
            parentQuickView.close();
          }
          const parentQuickAdd = form.closest('[data-quick-add]');
          if (parentQuickAdd) {
            parentQuickAdd.dataset.open = 'false';

            const trigger = parentQuickAdd.querySelector('[data-quick-add-trigger]');
            if (trigger) {
              trigger.setAttribute('aria-expanded', 'false');
            }
          }
          if (autoSubmitSection) {
            autoSubmitSection.dispatchEvent(new CustomEvent('pegasus:quick-add-reset'));
          }
          await refreshHeaderCartState({ openDrawer: true });
          announce(`${payload.product_title} added to cart.`);
        } catch (error) {
          if (loadingDrawer) {
            delete loadingDrawer.dataset.cartLoading;
          }
          announce(error.message || 'Unable to add item to cart.');
        } finally {
          if (autoSubmitSection) {
            delete autoSubmitSection.dataset.variantSubmitting;
            delete autoSubmitSection.dataset.loading;
          }

          if (button) {
            button.disabled = false;
            if (buttonLabel) {
              buttonLabel.textContent = originalLabel;
            } else {
              button.textContent = originalLabel;
            }
          }
        }
      });
    });
  }

  function initCartDrawerScrollbars(scope = document) {
    collect(scope, '[data-cart-drawer-scroll]').forEach((container) => {
      const viewport = container.querySelector('[data-cart-drawer-scroll-viewport]');
      const indicator = container.querySelector('[data-cart-drawer-scrollbar-indicator]');

      if (!viewport || !indicator) return;

      const update = () => {
        const viewportHeight = viewport.clientHeight;
        const contentHeight = viewport.scrollHeight;
        const maxScroll = Math.max(0, contentHeight - viewportHeight);
        const isScrollable = viewportHeight > 0 && maxScroll > 1;

        container.classList.toggle('is-scrollable', isScrollable);

        if (!isScrollable) {
          indicator.style.height = '';
          indicator.style.transform = 'translate3d(0, 0, 0)';
          return;
        }

        const indicatorHeight = Math.max(24, viewportHeight * (viewportHeight / contentHeight));
        const availableTrack = Math.max(0, viewportHeight - indicatorHeight);
        const indicatorOffset = maxScroll > 0
          ? availableTrack * (viewport.scrollTop / maxScroll)
          : 0;

        indicator.style.height = `${indicatorHeight}px`;
        indicator.style.transform = `translate3d(0, ${indicatorOffset}px, 0)`;
      };

      container.__updateCartDrawerScrollbar = update;

      if (container.dataset.cartDrawerScrollbarBound !== 'true') {
        container.dataset.cartDrawerScrollbarBound = 'true';
        viewport.addEventListener('scroll', update, { passive: true });

        if (typeof ResizeObserver === 'function') {
          const resizeObserver = new ResizeObserver(update);
          resizeObserver.observe(viewport);
          resizeObserver.observe(viewport.firstElementChild || viewport);
          container.__cartDrawerResizeObserver = resizeObserver;
        }

        if (typeof MutationObserver === 'function') {
          const mutationObserver = new MutationObserver(update);
          mutationObserver.observe(viewport, { childList: true, subtree: true });
          container.__cartDrawerMutationObserver = mutationObserver;
        }

        registerCleanup(container, () => {
          container.__cartDrawerResizeObserver?.disconnect();
          container.__cartDrawerMutationObserver?.disconnect();
        });
      }

      update();
    });
  }

  function setAnimatedDetailsState(details, shouldOpen, { animate = true } = {}) {
    const summary = details.querySelector(':scope > summary');

    if (!summary || details.open === shouldOpen && !detailsAnimations.has(details)) {
      details.open = shouldOpen;
      return Promise.resolve();
    }

    const activeAnimations = detailsAnimations.get(details);
    activeAnimations?.container?.cancel();
    activeAnimations?.content?.forEach((animation) => animation.cancel());
    detailsAnimations.delete(details);

    if (!animate || reducedMotionMedia.matches || typeof details.animate !== 'function') {
      details.open = shouldOpen;
      delete details.dataset.detailsAnimating;
      details.style.removeProperty('height');
      details.style.removeProperty('overflow');
      return Promise.resolve();
    }

    const startHeight = details.getBoundingClientRect().height;
    const contentElements = Array.from(details.children).filter((child) => child !== summary);

    details.style.height = `${startHeight}px`;
    details.style.overflow = 'clip';

    if (shouldOpen) details.open = true;

    const endHeight = shouldOpen ? details.scrollHeight : summary.getBoundingClientRect().height;
    const duration = 300;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const containerAnimation = details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration, easing }
    );
    const contentAnimations = contentElements.map((content) => content.animate(
      shouldOpen
        ? [
          { opacity: 0, transform: 'translateY(-6px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ]
        : [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: 'translateY(-5px)' },
        ],
      { duration: shouldOpen ? 260 : 190, easing, fill: 'both' }
    ));
    const animationState = { container: containerAnimation, content: contentAnimations };

    details.dataset.detailsAnimating = shouldOpen ? 'opening' : 'closing';
    detailsAnimations.set(details, animationState);

    const finish = () => {
      if (detailsAnimations.get(details) !== animationState) return;

      detailsAnimations.delete(details);
      if (!shouldOpen) details.open = false;
      contentAnimations.forEach((animation) => animation.cancel());
      delete details.dataset.detailsAnimating;
      details.style.removeProperty('height');
      details.style.removeProperty('overflow');
    };

    containerAnimation.addEventListener('finish', finish, { once: true });
    containerAnimation.addEventListener('cancel', () => {
      if (detailsAnimations.get(details) !== animationState) return;
      detailsAnimations.delete(details);
    }, { once: true });

    return containerAnimation.finished.catch(() => {});
  }

  function initAnimatedDetails(scope = document) {
    const selector = [
      '.theme-collapsible-tabs__item',
      '[data-product-accordion]',
      '.facets__group',
      '.customer-addresses__card',
      '.theme-header__drawer-simple-group',
    ].join(', ');

    collect(scope, selector).forEach((details) => {
      if (details.dataset.animatedDetailsBound === 'true') return;

      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      details.dataset.animatedDetailsBound = 'true';

      const handleClick = (event) => {
        const visuallyOpen = details.dataset.detailsAnimating === 'closing'
          ? false
          : details.open;

        event.preventDefault();
        setAnimatedDetailsState(details, !visuallyOpen);
      };

      summary.addEventListener('click', handleClick);
      registerCleanup(details, () => {
        const activeAnimations = detailsAnimations.get(details);
        activeAnimations?.container?.cancel();
        activeAnimations?.content?.forEach((animation) => animation.cancel());
        detailsAnimations.delete(details);
        summary.removeEventListener('click', handleClick);
      });
    });
  }

  function initExclusiveDetails(scope = document) {
    const groups = [
      {
        container: '.theme-collapsible-tabs__list',
        item: '.theme-collapsible-tabs__item',
      },
      {
        container: '.theme-product__info-column, .theme-product',
        item: '[data-product-accordion]',
      },
      {
        container: '.facets',
        item: '.facets__group',
      },
      {
        container: '.customer-addresses',
        item: '.customer-addresses__card',
      },
      {
        container: '.theme-header__drawer-simple-list',
        item: '.theme-header__drawer-simple-group',
      },
    ];

    groups.forEach(({ container: containerSelector, item: itemSelector }) => {
      collect(scope, containerSelector).forEach((container) => {
        const groupKey = `exclusiveDetailsBound${itemSelector.replace(/[^a-z0-9]/gi, '')}`;
        if (container.dataset[groupKey] === 'true') return;
        container.dataset[groupKey] = 'true';

        const getItems = () => collect(container, itemSelector).filter((item) => item.closest(containerSelector) === container);
        const initiallyOpen = getItems().filter((item) => item.open);

        initiallyOpen.slice(1).forEach((item) => {
          item.open = false;
        });

        const handleToggle = (event) => {
          const activeItem = event.target;
          if (!activeItem.matches(itemSelector) || !activeItem.open) return;

          getItems().forEach((item) => {
            if (item !== activeItem && item.open) setAnimatedDetailsState(item, false);
          });
        };

        container.addEventListener('toggle', handleToggle, true);
        registerCleanup(container, () => container.removeEventListener('toggle', handleToggle, true));
      });
    });
  }

  function initEmptyProductAppBlocks(scope = document) {
    collect(scope, '[class~="theme-product__block--@app"]').forEach((block) => {
      if (block.dataset.emptyAppBlockBound === 'true') return;

      const appBlock = block.querySelector('.shopify-app-block');
      if (!appBlock) return;

      block.dataset.emptyAppBlockBound = 'true';
      let updateFrame;

      const update = () => {
        block.classList.remove('is-empty-app-block');
        window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(() => {
          const hasVisibleText = appBlock.innerText.trim() !== '';
          const hasVisibleElement = [...appBlock.querySelectorAll('*')].some((element) => {
            if (['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(element.tagName)) return false;

            const styles = window.getComputedStyle(element);
            const bounds = element.getBoundingClientRect();

            return styles.display !== 'none'
              && styles.visibility !== 'hidden'
              && bounds.width > 0
              && bounds.height > 0;
          });

          block.classList.toggle('is-empty-app-block', !hasVisibleText && !hasVisibleElement);
        });
      };

      const mutationObserver = new MutationObserver(update);
      mutationObserver.observe(appBlock, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });

      const delayedUpdates = [250, 1000, 3000].map((delay) => window.setTimeout(update, delay));

      registerCleanup(block, () => {
        mutationObserver.disconnect();
        window.cancelAnimationFrame(updateFrame);
        delayedUpdates.forEach((timeout) => window.clearTimeout(timeout));
      });

      update();
    });
  }

  function initDraggableImageStacks(scope = document) {
    collect(scope, '[data-draggable-image-stack]').forEach((gallery) => {
      if (gallery.dataset.draggableInitialized === 'true') return;
      gallery.dataset.draggableInitialized = 'true';

      const controller = new AbortController();
      const items = [...gallery.querySelectorAll('[data-draggable-image]')];
      const dragLimit = 50;
      let activeDragController = null;
      const stackOrder = [...items];

      const clamp = (value) => Math.max(-dragLimit, Math.min(dragLimit, value));
      const resist = (value) => {
        if (Math.abs(value) <= dragLimit) return value;
        return Math.sign(value) * (dragLimit + ((Math.abs(value) - dragLimit) * 0.35));
      };

      const moveItem = (item, x, y) => {
        item.dataset.dragX = String(x);
        item.dataset.dragY = String(y);
        item.style.setProperty('--drag-x', `${x}px`);
        item.style.setProperty('--drag-y', `${y}px`);
      };

      const applyStackOrder = () => {
        stackOrder.forEach((stackItem, index) => {
          stackItem.style.zIndex = String(items.length - index + 3);
        });
      };

      const sendToBack = (item, velocityX = 0, velocityY = 0) => {
        const currentX = Number.parseFloat(item.dataset.dragX) || 0;
        const currentY = Number.parseFloat(item.dataset.dragY) || 0;
        const projectedX = clamp(currentX + (velocityX * 90));
        const projectedY = clamp(currentY + (velocityY * 90));

        item.classList.remove('is-dragging');
        item.classList.add('is-settling');
        moveItem(item, projectedX, projectedY);

        const currentOrderIndex = stackOrder.indexOf(item);
        if (currentOrderIndex >= 0) stackOrder.splice(currentOrderIndex, 1);
        stackOrder.push(item);
        applyStackOrder();

        window.setTimeout(() => item.classList.remove('is-settling'), 520);
      };

      applyStackOrder();

      items.forEach((item) => {
        item.dataset.dragX = item.dataset.dragX || '0';
        item.dataset.dragY = item.dataset.dragY || '0';

        item.addEventListener('pointerdown', (event) => {
          if (event.button !== 0 && event.pointerType === 'mouse') return;

          event.preventDefault();
          activeDragController?.abort();
          activeDragController = new AbortController();

          const startX = event.clientX;
          const startY = event.clientY;
          const pointerId = event.pointerId;
          const originX = Number.parseFloat(item.dataset.dragX) || 0;
          const originY = Number.parseFloat(item.dataset.dragY) || 0;
          let previousX = originX;
          let previousY = originY;
          let previousTime = event.timeStamp;
          let velocityX = 0;
          let velocityY = 0;

          item.style.zIndex = String(items.length + 20);
          item.classList.add('is-dragging');
          item.classList.remove('is-settling');

          try {
            item.setPointerCapture(pointerId);
          } catch (error) {
            // Window-level listeners below keep dragging continuous when pointer capture is unavailable.
          }

          const handleMove = (moveEvent) => {
            if (moveEvent.pointerId !== pointerId) return;

            moveEvent.preventDefault();
            const nextX = resist(originX + moveEvent.clientX - startX);
            const nextY = resist(originY + moveEvent.clientY - startY);
            const elapsed = Math.max(16, moveEvent.timeStamp - previousTime);

            velocityX = (nextX - previousX) / elapsed;
            velocityY = (nextY - previousY) / elapsed;
            previousX = nextX;
            previousY = nextY;
            previousTime = moveEvent.timeStamp;
            moveItem(item, nextX, nextY);
          };

          const handleEnd = (endEvent) => {
            if (endEvent?.pointerId != null && endEvent.pointerId !== pointerId) return;

            try {
              if (item.hasPointerCapture(pointerId)) item.releasePointerCapture(pointerId);
            } catch (error) {
              // The browser may already have released capture after leaving the original element.
            }

            activeDragController?.abort();
            activeDragController = null;
            sendToBack(item, velocityX, velocityY);
          };

          window.addEventListener('pointermove', handleMove, { passive: false, signal: activeDragController.signal });
          window.addEventListener('pointerup', handleEnd, { signal: activeDragController.signal });
          window.addEventListener('pointercancel', handleEnd, { signal: activeDragController.signal });
          window.addEventListener('blur', handleEnd, { once: true, signal: activeDragController.signal });
        }, { signal: controller.signal });

        item.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            item.style.zIndex = String(items.length + 20);
            sendToBack(item);
            return;
          }

          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;

          event.preventDefault();
          const step = event.shiftKey ? 30 : 10;
          const currentX = Number.parseFloat(item.dataset.dragX) || 0;
          const currentY = Number.parseFloat(item.dataset.dragY) || 0;
          const nextX = clamp(currentX + (event.key === 'ArrowLeft' ? -step : (event.key === 'ArrowRight' ? step : 0)));
          const nextY = clamp(currentY + (event.key === 'ArrowUp' ? -step : (event.key === 'ArrowDown' ? step : 0)));

          item.style.zIndex = String(items.length + 20);
          moveItem(item, nextX, nextY);
        }, { signal: controller.signal });
      });

      registerCleanup(gallery, () => {
        activeDragController?.abort();
        controller.abort();
      });
    });
  }

  function initFloatingMediaGroupContainment(scope = document) {
    collect(scope, '.group:has(.basic-content-floating-image), .basic-content-group:has(.basic-content-floating-image)').forEach((group) => {
      if (group.dataset.floatingMediaContainmentInitialized === 'true') return;
      group.dataset.floatingMediaContainmentInitialized = 'true';

      const content = group.querySelector(':scope > .group__content, :scope > .basic-content-group__content');
      if (!content) return;

      const controller = new AbortController();
      const observedMedia = new WeakSet();
      const resizeObserver = typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => schedule())
        : null;
      let updateFrame = null;

      const getFloatingMedia = () => [...group.querySelectorAll('.basic-content-floating-image')].filter((media) => {
        if (media.closest('.group, .basic-content-group') !== group) return false;

        return media.parentElement === content || media.parentElement?.parentElement === content;
      });

      const getMotionTravel = (media) => {
        const styles = window.getComputedStyle(media);
        if (styles.animationName === 'none') return 0;

        const drift = Number.parseFloat(styles.getPropertyValue('--floating-image-drift-y')) || 0;
        return Math.abs(drift) + 8;
      };

      const observeMedia = (media) => {
        if (observedMedia.has(media)) return;
        observedMedia.add(media);
        resizeObserver?.observe(media);

        media.querySelectorAll('img, video').forEach((asset) => {
          asset.addEventListener('load', schedule, { signal: controller.signal });
          asset.addEventListener('loadedmetadata', schedule, { signal: controller.signal });
        });
      };

      const update = () => {
        updateFrame = null;
        const floatingMedia = getFloatingMedia();

        if (floatingMedia.length === 0) {
          group.style.removeProperty('--floating-media-containment-height');
          return;
        }

        let containmentCoreHeight = 0;
        let topInset = 0;
        let bottomInset = 0;

        floatingMedia.forEach((media) => {
          observeMedia(media);
          const mediaHeight = media.offsetHeight;
          const mediaWidth = media.offsetWidth;
          const motionTravel = getMotionTravel(media);
          const styles = window.getComputedStyle(media);
          const offsetY = Number.parseFloat(styles.getPropertyValue('--floating-image-offset-y')) || 0;
          const rotation = Number.parseFloat(styles.getPropertyValue('--floating-image-rotation')) || 0;
          const verticalAlignment = styles.getPropertyValue('--floating-image-vertical-alignment').trim();
          const rotationRadians = rotation * (Math.PI / 180);
          const rotatedHeight = (
            Math.abs(mediaHeight * Math.cos(rotationRadians))
            + Math.abs(mediaWidth * Math.sin(rotationRadians))
          );

          let offsetSpace = 0;
          if (verticalAlignment === 'center') {
            offsetSpace = Math.abs(offsetY) * 2;
          } else if (verticalAlignment === 'top') {
            offsetSpace = Math.max(offsetY, 0);
            topInset = Math.max(topInset, Math.max(-offsetY, 0));
          } else if (verticalAlignment === 'bottom') {
            offsetSpace = Math.max(-offsetY, 0);
            bottomInset = Math.max(bottomInset, Math.max(offsetY, 0));
          }

          const requiredHeight = Math.max(mediaHeight, rotatedHeight) + (motionTravel * 2) + offsetSpace;

          containmentCoreHeight = Math.max(containmentCoreHeight, requiredHeight);
          media.style.removeProperty('--floating-media-contained-top');
        });

        const containmentHeight = containmentCoreHeight + topInset + bottomInset;
        const nextHeight = `${Math.ceil(containmentHeight)}px`;
        if (group.style.getPropertyValue('--floating-media-containment-height') !== nextHeight) {
          group.style.setProperty('--floating-media-containment-height', nextHeight);
        }

        floatingMedia.forEach((media) => {
          const verticalAlignment = window.getComputedStyle(media)
            .getPropertyValue('--floating-image-vertical-alignment')
            .trim();
          let anchorShift = 0;

          if (verticalAlignment === 'top') {
            anchorShift = topInset;
          } else if (verticalAlignment === 'bottom') {
            anchorShift = -bottomInset;
          } else {
            anchorShift = (topInset - bottomInset) / 2;
          }

          const nextShift = `${Math.round(anchorShift * 100) / 100}px`;
          if (media.style.getPropertyValue('--floating-media-anchor-shift-y') !== nextShift) {
            media.style.setProperty('--floating-media-anchor-shift-y', nextShift);
          }
        });

      };

      function schedule() {
        window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(update);
      }

      const mutationObserver = typeof MutationObserver === 'function'
        ? new MutationObserver(schedule)
        : null;

      mutationObserver?.observe(group, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true,
      });

      window.addEventListener('resize', schedule, { passive: true, signal: controller.signal });

      registerCleanup(group, () => {
        controller.abort();
        mutationObserver?.disconnect();
        resizeObserver?.disconnect();
        window.cancelAnimationFrame(updateFrame);
        group.style.removeProperty('--floating-media-containment-height');
        getFloatingMedia().forEach((media) => {
          media.style.removeProperty('--floating-media-contained-top');
          media.style.removeProperty('--floating-media-anchor-shift-y');
        });
      });

      schedule();
    });
  }

  function initOffsetImageGroupContainment(scope = document) {
    collect(scope, '.group:has(.theme-content-image, .basic-content__block--image), .basic-content-group:has(.theme-content-image, .basic-content__block--image)').forEach((group) => {
      if (group.dataset.offsetImageContainmentInitialized === 'true') return;
      group.dataset.offsetImageContainmentInitialized = 'true';

      const controller = new AbortController();
      const content = group.querySelector(':scope > .group__content, :scope > .basic-content-group__content');
      let updateFrame = null;

      if (!content) return;

      const getImages = () => [...content.querySelectorAll('.theme-content-image, .basic-content__block--image')].filter((image) => (
        image.closest('.group, .basic-content-group') === group
      ));

      const update = () => {
        const suffix = window.matchMedia('(min-width: 768px)').matches ? 'desktop' : 'mobile';
        const containment = { top: 0, right: 0, bottom: 0, left: 0 };

        getImages().forEach((image) => {
          const styles = window.getComputedStyle(image);
          const prefix = image.classList.contains('theme-content-image')
            ? '--content-image-offset-'
            : '--basic-image-offset-';

          Object.keys(containment).forEach((edge) => {
            const value = Number.parseFloat(styles.getPropertyValue(`${prefix}${edge}-${suffix}`)) || 0;
            containment[edge] = Math.max(containment[edge], value);
          });
        });

        Object.entries(containment).forEach(([edge, value]) => {
          const property = `--offset-image-containment-${edge}`;
          const nextValue = `${value}px`;
          if (group.style.getPropertyValue(property) !== nextValue) {
            group.style.setProperty(property, nextValue);
          }
        });
      };

      const schedule = () => {
        window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(update);
      };

      const mutationObserver = typeof MutationObserver === 'function'
        ? new MutationObserver(schedule)
        : null;

      mutationObserver?.observe(group, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true,
      });

      window.addEventListener('resize', schedule, { passive: true, signal: controller.signal });

      registerCleanup(group, () => {
        controller.abort();
        mutationObserver?.disconnect();
        window.cancelAnimationFrame(updateFrame);
        ['top', 'right', 'bottom', 'left'].forEach((edge) => {
          group.style.removeProperty(`--offset-image-containment-${edge}`);
        });
      });

      schedule();
    });
  }

  function initThirdPartyAccessibility(scope = document) {
    collect(scope, '#judgeme_product_reviews').forEach((widget) => {
      if (widget.dataset.themeAccessibilityInitialized === 'true') return;
      widget.dataset.themeAccessibilityInitialized = 'true';

      const updateReviewLink = () => {
        const reviewLink = widget.querySelector('.jm-average-rating-display[aria-label]');
        if (!reviewLink) return false;

        reviewLink.removeAttribute('aria-label');
        return true;
      };

      if (updateReviewLink() || typeof MutationObserver !== 'function') return;

      const mutationObserver = new MutationObserver(() => {
        if (updateReviewLink()) mutationObserver.disconnect();
      });
      mutationObserver.observe(widget, { childList: true, subtree: true });
      registerCleanup(widget, () => mutationObserver.disconnect());
    });
  }

  function initScope(scope = document) {
    initUpcartBridge(scope);
    initDialogs(scope);
    initButtonVideoModals(scope);
    initHeaderMenuButtons(scope);
    initHeaderMenus(scope);
    initStandardMegaMenus(scope);
    initDrawerMenus(scope);
    initPredictiveSearch(scope);
    initQuickAdd(scope);
    initDeferredImages(scope);
    initImageReveals(scope);
    initMediaFades(scope);
    initFloatingMediaGroupContainment(scope);
    initOffsetImageGroupContainment(scope);
    initThirdPartyAccessibility(scope);
    initAnimations(scope);
    initSmoothScroll();
    initStickyHeaders(scope);
    initFullWidthScrollCards(scope);
    initAutoSubmit(scope);
    initKlaviyoNewsletterForms(scope);
    initCustomerAddressForms(scope);
    initAjaxCart(scope);
    initCartDrawer(scope);
    initCartDrawerScrollbars(scope);
    initAnimatedDetails(scope);
    initExclusiveDetails(scope);
    initEmptyProductAppBlocks(scope);
    initDraggableImageStacks(scope);
    loadConditionalFeatures(scope);
    initRegisteredFeatures(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initScope(document);
      window.requestAnimationFrame(() => initHeaderMenuButtons(document));
    });
  } else {
    initScope(document);
    window.requestAnimationFrame(() => initHeaderMenuButtons(document));
  }

  window.addEventListener('load', () => initHeaderMenuButtons(document), { once: true });
  window.addEventListener('load', () => window.ScrollTrigger?.refresh(), { once: true });

  document.addEventListener('shopify:section:load', (event) => {
    initScope(event.target);
    window.ScrollTrigger?.refresh();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupScope(event.target);
  });

  window.PegasusTheme = {
    buildStoreProductUrl,
    collect,
    escapeHtml,
    extractStoreProductImage,
    fetchSectionMarkup,
    getLocaleRoot,
    registerCleanup,
    registerSmoothScrollUpdate,
    registerFeature,
    announce,
    fetchCart,
    formatMoney,
    init: initScope,
    openCartDrawerLoading,
    refreshHeaderCartState,
    updateCartCount,
  };

  document.dispatchEvent(new CustomEvent('pegasus:theme:ready', { detail: window.PegasusTheme }));
})();
